import type { Href } from "expo-router";
import { useFocusEffect, useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Linking,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type ViewStyle,
} from "react-native";

import { COLORS, FONTS } from "../../../../../lib/brand";
import { PANE_THEMES } from "../../../../../components/brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;

// StudentProgramShell is rendered by _layout.tsx; this file just provides
// the body content.
import { HeaderOverlay } from "../../../../../components/student/HeaderOverlay";
import {
  EyeIcon,
  LeaveIcon,
  Padlock,
  TeamIconButton,
  TrashIcon,
} from "../../../../../components/student/TeamControlIcons";
import { TeamPitchSection } from "../../../../../components/student/TeamPitchSection";
import { TeamInviteSection } from "../../../../../components/student/TeamInviteSection";
import { PeerProfileOverlay } from "../../../../../components/student/PeerProfileOverlay";
import { Avatar, Button, EmptyState } from "../../../../../components/ui";
import { useAuth } from "../../../../../lib/authContext";
import {
  getTeamDetail,
  promoteTeamMemberToCaptain,
  type TeamDetail,
  type TeamDetailMember,
} from "../../../../../lib/teamDetail";
import {
  deleteTeamMessage,
  getMyLastReadAt,
  listPinnedMessages,
  listTeamMessages,
  markTeamRead,
  pinTeamMessage,
  sendTeamMessage,
  subscribeToTeamMessageEdits,
  subscribeToTeamMessages,
  unpinTeamMessage,
  type PinnedMessage,
  type TeamMessage,
} from "../../../../../lib/teamMessages";
import { clearCachedMyTeam, writeCachedMyTeam } from "../../../../../lib/myTeamCache";
import { useCompetitionShell } from "../../../../../components/student/CompetitionShellContext";
import {
  clearCachedTeamDetail,
  readCachedTeamDetail,
  writeCachedTeamDetail,
} from "../../../../../lib/teamDetailCache";
import {
  clearCachedTeamChat,
  readCachedTeamChat,
  writeCachedTeamChat,
} from "../../../../../lib/teamChatCache";
import {
  listTeamEvents,
  subscribeToTeamEvents,
  type TeamEvent,
} from "../../../../../lib/teamEvents";
import {
  captainDeleteAnnouncement,
  listMyAnnouncements,
  type MyAnnouncement,
} from "../../../../../lib/announcements";
import { AnnouncementChatEvent } from "../../../../../components/student/AnnouncementChatEvent";
import { ProgramAnnouncementsOverlay } from "../../../../../components/student/dashboard/ProgramAnnouncementsOverlay";
import { MegaphoneCompose } from "../../../../../components/client/MegaphoneCompose";
import {
  deleteDmMessage,
  type DmMessage,
  type DmThreadForTeam,
  getMyDmLastReadAt,
  getMyDmThreadsForTeam,
  listDmMessages,
  markDmRead,
  openOrCreateDmThread,
  sendDmMessage,
  subscribeToDmMessages,
  subscribeToDmUnreadSignals,
} from "../../../../../lib/dms";
import {
  acceptJoinRequest,
  declineJoinRequest,
  deleteTeam,
  ignoreTeamRequester,
  kickMember,
  leaveTeam,
  listPendingJoinRequests,
  lockTeam,
  setTeamVisibility,
} from "../../../../../lib/teams";
import Svg, { Path } from "react-native-svg";
import type { PendingJoinRequest } from "../../../../../types/database";

// Plum palette — used for pinned-message accents (banner + dot + icon).
// Picked over blue because blue collided with the link/URL color and
// because the user explicitly nixed blue for pin treatments.
const PLUM = "#845A8C";
const PLUM_DEEP = "#5E3F66";
const PLUM_BG = "rgba(132,90,140,0.10)";
const PLUM_BORDER = "rgba(132,90,140,0.40)";

const pillWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function compStatusLabel(s: TeamDetail["competition"]["status"]): { label: string; tint: "amber" | "forest" | "muted" } {
  if (s === "open") return { label: "In progress", tint: "forest" };
  if (s === "judging") return { label: "Judging", tint: "amber" };
  if (s === "closed") return { label: "Closed", tint: "muted" };
  return { label: "Draft", tint: "muted" };
}

const CHAT_WORD_CAP = 250;

/** Counts whitespace-separated tokens. Empty input → 0. */
function countWords(s: string): number {
  const trimmed = s.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/u).length;
}

/**
 * Truncate `next` to at most `cap` words, preserving the leading whitespace
 * the user typed (so they don't fight the autocomplete cursor). When the cap
 * is hit, returns the previous text instead — this prevents the input from
 * silently dropping characters mid-word.
 */
function clampToWordCap(prev: string, next: string, cap: number): string {
  const count = countWords(next);
  if (count <= cap) return next;
  return prev;
}

export default function StudentTeamDetailScreen() {
  // _layout.tsx owns the StudentProgramShell + the shared competition/team
  // fetch. This file just reads the resolved team id (and whether the load
  // has settled yet) out of context. No own lookup, no own shell.
  const { competitionId, myTeam, hasLoaded } = useCompetitionShell();
  const params = useLocalSearchParams<{ dm?: string | string[] }>();
  const router = useRouter();
  const { user } = useAuth();
  const myUserId = user?.id ?? null;

  const teamId = myTeam?.team.id ?? "";
  const teamLookupLoading = !hasLoaded && !teamId;
  const noTeamYet = hasLoaded && !teamId;

  // Read the chat cache once on mount. Subsequent visits to the same team
  // hydrate messages/events/pins from this snapshot so the chat panel paints
  // instantly instead of flashing the small spinner while reloadChat() runs.
  // Network fetch still wins eventually — the cache is just the first frame.
  const [initialChat] = useState<{
    messages: TeamMessage[];
    events: TeamEvent[];
    pins: PinnedMessage[];
  } | null>(() => {
    if (!myUserId || !teamId) return null;
    return readCachedTeamChat(myUserId, teamId) ?? null;
  });

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<TeamDetail | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [confirmingPromote, setConfirmingPromote] = useState<
    { studentId: string; name: string } | null
  >(null);
  const [confirmingKick, setConfirmingKick] = useState<
    { studentId: string; name: string } | null
  >(null);
  const [pitchOverlayOpen, setPitchOverlayOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  // When set, the PeerProfileOverlay opens with this teammate's profile.
  // Drives clicks on roster avatars, chat-bubble avatars, DM avatars, and
  // pending-request avatars/names. Null means closed.
  const [peerProfileFor, setPeerProfileFor] = useState<string | null>(null);

  const [pending, setPending] = useState<PendingJoinRequest[]>([]);
  const [messages, setMessages] = useState<TeamMessage[]>(() => initialChat?.messages ?? []);
  const [events, setEvents] = useState<TeamEvent[]>(() => initialChat?.events ?? []);
  // Competition-scoped announcements rendered inline among chat messages
  // as green event rows. Filtered client-side from listMyAnnouncements().
  const [announcements, setAnnouncements] = useState<MyAnnouncement[]>([]);
  const [announcementsOverlayOpen, setAnnouncementsOverlayOpen] = useState(false);
  const [chatLoading, setChatLoading] = useState(() => !initialChat);
  // Once we've snapped the chat to the bottom for this visit we leave
  // subsequent scrolls alone (so a user reading higher up doesn't get yanked
  // back down whenever a new message arrives).
  const initialScrollDoneRef = useRef(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  // Reply target — when set, the composer renders a quoted preview and the
  // next send carries reply_to_message_id. Cleared on send or by ✕.
  const [replyTo, setReplyTo] = useState<TeamMessage | null>(null);
  // Currently-pinned messages (newest first; max 5 by RPC). Loaded with
  // chat and refreshed on pin/unpin/delete signals.
  const [pinned, setPinned] = useState<PinnedMessage[]>(() => initialChat?.pins ?? []);
  // True when the pinned banner is expanded to show all pins (not just the
  // top one). Reset whenever the team or pin set changes meaningfully.
  const [pinsExpanded, setPinsExpanded] = useState(false);
  // Optional: when set briefly, the chat scrolls/highlights this id (used
  // when a pin is clicked). The cleanup timer wipes the highlight.
  const [highlightMessageId, setHighlightMessageId] = useState<string | null>(null);
  // Captain-only megaphone modal — opens from the icon in the team chat
  // header. Sends an announcement scoped to this competition so it lands
  // in the dashboard card, the program announcements overlay, and inline
  // in every team's chat as the green sage event card.
  const [megaphoneOpen, setMegaphoneOpen] = useState(false);

  // Snapshot of the persisted "last read" timestamp taken once on entry to
  // the chat. The unread divider renders against THIS snapshot for the whole
  // session, so even after the arrow / scrolling marks newer messages as
  // read in storage, the divider stays put until the user leaves the team
  // tab and comes back.
  const [sessionUnreadCutoff, setSessionUnreadCutoff] = useState<string | null>(null);
  // Becomes true once getMyLastReadAt has resolved for the current team.
  // Gates the unread-divider / jump-to-unread computation so the arrow
  // doesn't flash on entry: cached messages render synchronously, but the
  // persisted last_read_at is async — without this gate, the brief window
  // where cutoff defaults to 0 makes every message look unread for ~200ms.
  const [cutoffLoaded, setCutoffLoaded] = useState(false);
  // Tracks the chat ScrollView's vertical offset and dimensions so we can
  // tell whether the unread divider is currently above the viewport.
  const [chatScrolledToBottom, setChatScrolledToBottom] = useState(true);
  const [chatScrollY, setChatScrollY] = useState(0);
  const [unreadAnchorY, setUnreadAnchorY] = useState<number | null>(null);
  // Once the user clicks the Jump-to-unread arrow we suppress it for the
  // rest of this visit. Scrolling back up past the divider should NOT bring
  // the arrow back. Resets when the user enters a different team.
  const [arrowDismissed, setArrowDismissed] = useState(false);

  // Link-warning modal: when a user clicks a URL inside someone else's
  // chat message, we open this confirm before letting the browser handle
  // the navigation. Lives at page level so the modal doesn't get tied to
  // a particular ChatMessageGroup.
  const [linkConfirm, setLinkConfirm] = useState<{ url: string; sender: string } | null>(null);

  // ---- Direct-message state -------------------------------------------------
  // myDmThreads: per-other-user snapshot of thread id + unread count for this
  // team. Drives the chat-bubble icon badges in the roster. Re-fetched on
  // mount and on every dm-unread realtime signal.
  const [myDmThreads, setMyDmThreads] = useState<DmThreadForTeam[]>([]);
  // openDmWith: when non-null the roster panel content is replaced by the DM
  // chat with this teammate. studentId is the other user; threadId is loaded
  // lazily after click.
  const [openDmWith, setOpenDmWith] = useState<{ studentId: string; name: string; avatarUrl: string | null; threadId: string | null } | null>(null);
  const [dmMessages, setDmMessages] = useState<DmMessage[]>([]);
  const [dmLoading, setDmLoading] = useState(false);
  const [dmDraft, setDmDraft] = useState("");
  const [dmSending, setDmSending] = useState(false);
  const dmScrollRef = useRef<ScrollView | null>(null);
  const [dmAtBottom, setDmAtBottom] = useState(true);
  // Reply target inside the DM panel — when set, the next send carries
  // reply_to_message_id and a quoted-preview pill renders above the
  // composer. Cleared on send or by ✕.
  const [dmReplyTo, setDmReplyTo] = useState<DmMessage | null>(null);
  // ---- DM unread divider + jump-to-unread arrow state -----------------------
  // sessionUnreadCutoff: snapshotted on entry to a thread so the divider
  // doesn\'t move mid-session. Same pattern as the team chat.
  const [dmSessionUnreadCutoff, setDmSessionUnreadCutoff] = useState<string | null>(null);
  // Same role as `cutoffLoaded` for the team chat — gates the DM divider /
  // arrow until getMyDmLastReadAt has resolved so we don't flash an unread
  // arrow on entry against a not-yet-loaded cutoff.
  const [dmCutoffLoaded, setDmCutoffLoaded] = useState(false);
  const [dmUnreadAnchorY, setDmUnreadAnchorY] = useState<number | null>(null);
  const [dmChatScrollY, setDmChatScrollY] = useState(0);
  const [dmArrowDismissed, setDmArrowDismissed] = useState(false);

  const scrollRef = useRef<ScrollView | null>(null);
  const chatScrollRef = useRef<ScrollView | null>(null);
  // Refs for the team-chat + DM composers so we can auto-focus when the
  // user clicks Reply (no extra click on the textbox needed).
  const teamComposerRef = useRef<TextInput | null>(null);
  const dmComposerRef = useRef<TextInput | null>(null);

  // Auto-focus the team chat composer whenever a reply is staged.
  useEffect(() => {
    if (!replyTo) return;
    const id = setTimeout(() => teamComposerRef.current?.focus(), 30);
    return () => clearTimeout(id);
  }, [replyTo]);

  // Auto-focus the DM composer whenever a DM reply is staged.
  useEffect(() => {
    if (!dmReplyTo) return;
    const id = setTimeout(() => dmComposerRef.current?.focus(), 30);
    return () => clearTimeout(id);
  }, [dmReplyTo]);

  const reload = useCallback(async () => {
    if (!teamId) return;
    setErrorText(null);
    // Don't toggle loading=true unconditionally — if we already have detail
    // (from cache or a previous fetch in this mount) we want to refresh
    // silently in the background instead of flashing a full-page spinner.
    // Cold loads still show the spinner because `loading` starts true and
    // we only flip it to false after the fetch lands.
    const { detail: d, error } = await getTeamDetail(teamId);
    if (error || !d) {
      setErrorText(error?.message ?? "Team not found");
      setDetail(null);
      setLoading(false);
      return;
    }
    setDetail(d);
    setLoading(false);

    // Prime the myTeam cache so navigating from here to dashboard /
    // deliverables / info paints the "Team" label on the first frame
    // instead of flashing "Find Team" while the workspace re-fetches.
    if (myUserId && d.competition?.id && d.team?.id) {
      writeCachedMyTeam(myUserId, d.competition.id, { teamId: d.team.id });
    }
    // And persist the full TeamDetail so the NEXT time the user opens this
    // team page it can paint instantly from cache instead of showing a
    // spinner while getTeamDetail re-fetches.
    if (myUserId) {
      writeCachedTeamDetail(myUserId, teamId, d);
    }

    // Load pending requests if captain.
    if (d.is_captain) {
      const pr = await listPendingJoinRequests(teamId);
      setPending(pr);
    } else {
      setPending([]);
    }
  }, [teamId, myUserId]);

  // Synchronously hydrate from the team-detail cache as soon as we know
  // who the user is. Runs before reload() can complete, so the chrome
  // (header, roster, captain controls) paints on the first frame instead
  // of waiting on the network round-trip. Only seeds — the network fetch
  // still runs and overwrites with fresh data.
  useEffect(() => {
    if (!myUserId || !teamId) return;
    const cached = readCachedTeamDetail(myUserId, teamId);
    if (cached) {
      setDetail((prev) => prev ?? cached);
      setLoading(false);
    }
  }, [myUserId, teamId]);

  const reloadChat = useCallback(async () => {
    if (!teamId) return;
    // Don't toggle chatLoading=true unconditionally — if we already have
    // messages on screen (from cache or a previous fetch this mount), refresh
    // silently so the panel doesn't flash a spinner mid-session. Cold loads
    // still show the spinner because chatLoading starts true when there's no
    // cached chat to seed from.
    initialScrollDoneRef.current = false;
    const [msgs, evts, pins] = await Promise.all([
      listTeamMessages(teamId, 200),
      listTeamEvents(teamId, 200),
      listPinnedMessages(teamId),
    ]);
    setMessages(msgs);
    setEvents(evts);
    setPinned(pins);
    setChatLoading(false);
    if (myUserId) {
      writeCachedTeamChat(myUserId, teamId, { messages: msgs, events: evts, pins });
    }
    // The real "snap to bottom" happens via onContentSizeChange below — that
    // fires once the message rows have actually been measured. We still kick
    // off a few setTimeout snaps as a belt-and-braces fallback for browsers
    // where the size change is reported late.
    const snap = () => chatScrollRef.current?.scrollToEnd({ animated: false });
    setTimeout(snap, 30);
    setTimeout(snap, 120);
    setTimeout(snap, 280);
  }, [teamId, myUserId]);

  useFocusEffect(
    useCallback(() => {
      void reload();
    }, [reload])
  );

  // Fetch competition-scoped announcements once we know the competition.
  // Refreshes on reload (focus) so newly-published announcements appear
  // without a hard refresh.
  useEffect(() => {
    const compId = detail?.competition?.id;
    if (!compId) {
      setAnnouncements([]);
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const all = await listMyAnnouncements();
        if (!alive) return;
        // Team chat only surfaces announcements explicitly scoped to
        // THIS competition. Global ("all") admin announcements live in
        // the activity bell only — they don't belong inline in chat.
        setAnnouncements(
          all.filter(
            (a) =>
              a.audience_kind === "competition" && a.audience_competition_id === compId
          )
        );
      } catch {
        if (alive) setAnnouncements([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [detail?.competition?.id]);

  useEffect(() => {
    if (!teamId) return;
    void reloadChat();
    const unsubMsg = subscribeToTeamMessages(teamId, (msg) => {
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      setTimeout(() => {
        chatScrollRef.current?.scrollToEnd({ animated: true });
      }, 50);
    });
    const unsubEvt = subscribeToTeamEvents(teamId, (evt) => {
      setEvents((prev) => {
        if (prev.some((e) => e.id === evt.id)) return prev;
        return [...prev, evt];
      });
      setTimeout(() => {
        chatScrollRef.current?.scrollToEnd({ animated: true });
      }, 50);
    });
    // Pin/unpin/soft-delete signals — refetch the message list + the pin
    // list together. Lightweight: ~2 round-trips, debounced naturally by
    // realtime batching.
    const unsubEdits = subscribeToTeamMessageEdits(teamId, () => {
      void (async () => {
        const [msgs, pins] = await Promise.all([
          listTeamMessages(teamId, 200),
          listPinnedMessages(teamId),
        ]);
        setMessages(msgs);
        setPinned(pins);
      })();
    });
    return () => {
      unsubMsg();
      unsubEvt();
      unsubEdits();
    };
  }, [teamId, reloadChat]);

  // Load my DM thread snapshot for this team and re-fetch on any dm-unread
  // realtime signal. RLS scopes the result so this is safe to subscribe at
  // page level.
  useEffect(() => {
    if (!teamId) return;
    let alive = true;
    const reloadDms = async () => {
      const rows = await getMyDmThreadsForTeam(teamId);
      if (alive) setMyDmThreads(rows);
    };
    void reloadDms();
    const unsub = subscribeToDmUnreadSignals(() => {
      void reloadDms();
    });
    return () => {
      alive = false;
      unsub();
    };
  }, [teamId]);

  // When a DM is open, load its messages + subscribe to inserts. Read-cursor
  // is moved every time we land at the bottom (mirrors the team-chat pattern).
  useEffect(() => {
    if (!openDmWith?.threadId) return;
    const tid = openDmWith.threadId;
    let alive = true;
    setDmLoading(true);
    void (async () => {
      const msgs = await listDmMessages(tid, 200);
      if (!alive) return;
      setDmMessages(msgs);
      setDmLoading(false);
      setTimeout(() => dmScrollRef.current?.scrollToEnd({ animated: false }), 30);
    })();
    const unsub = subscribeToDmMessages(tid, (m) => {
      setDmMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
      setTimeout(() => dmScrollRef.current?.scrollToEnd({ animated: true }), 50);
    });
    return () => {
      alive = false;
      unsub();
    };
  }, [openDmWith?.threadId]);

  // Snapshot my last_read_at for this thread once on entry. Reset the
  // unread anchor + arrow-dismissed state so the new thread starts clean.
  useEffect(() => {
    if (!openDmWith?.threadId) {
      setDmSessionUnreadCutoff(null);
      setDmUnreadAnchorY(null);
      setDmArrowDismissed(false);
      setDmCutoffLoaded(false);
      return;
    }
    const tid = openDmWith.threadId;
    setDmUnreadAnchorY(null);
    setDmArrowDismissed(false);
    setDmCutoffLoaded(false);
    let alive = true;
    void (async () => {
      const iso = await getMyDmLastReadAt(tid);
      if (alive) {
        setDmSessionUnreadCutoff(iso);
        setDmCutoffLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [openDmWith?.threadId]);

  // First unread DM message id, computed against the SESSION cutoff so the
  // divider stays put for the whole visit. Null cutoff (never read) → treat
  // as epoch so every non-self message is unread.
  const dmFirstUnreadId = useMemo(() => {
    if (!openDmWith) return null;
    // Same gate as the team chat: don't compute until getMyDmLastReadAt has
    // resolved or the arrow flashes briefly on entry against a stale cutoff.
    if (!dmCutoffLoaded) return null;
    const cutoff = dmSessionUnreadCutoff
      ? new Date(dmSessionUnreadCutoff).getTime()
      : 0;
    if (Number.isNaN(cutoff)) return null;
    for (const m of dmMessages) {
      if (m.sender_id === myUserId) continue;
      if (new Date(m.created_at).getTime() > cutoff) return m.id;
    }
    return null;
  }, [dmMessages, dmSessionUnreadCutoff, myUserId, openDmWith, dmCutoffLoaded]);

  const showJumpToDmUnread =
    !dmArrowDismissed &&
    dmFirstUnreadId != null &&
    dmUnreadAnchorY != null &&
    dmChatScrollY > dmUnreadAnchorY + 8;

  const onJumpToDmUnread = useCallback(() => {
    setDmArrowDismissed(true);
    if (dmUnreadAnchorY == null || !dmScrollRef.current) return;
    dmScrollRef.current.scrollTo({
      y: Math.max(0, dmUnreadAnchorY - 12),
      animated: true,
    });
    if (openDmWith?.threadId && dmMessages.length > 0) {
      const newest = dmMessages[dmMessages.length - 1];
      if (newest?.created_at) void markDmRead(openDmWith.threadId, newest.created_at);
    }
  }, [openDmWith, dmMessages, dmUnreadAnchorY]);

  // Auto-mark-as-read whenever we are at the bottom of the open DM.
  useEffect(() => {
    if (!openDmWith?.threadId || !dmAtBottom || dmMessages.length === 0) return;
    const newest = dmMessages[dmMessages.length - 1];
    if (newest?.created_at) void markDmRead(openDmWith.threadId, newest.created_at);
  }, [openDmWith?.threadId, dmAtBottom, dmMessages]);

  // Hard-eject from the DM if the other member is no longer on the team.
  useEffect(() => {
    if (!openDmWith || !detail) return;
    const stillHere = detail.members.some((m) => m.student_id === openDmWith.studentId);
    if (!stillHere) {
      setOpenDmWith(null);
      setDmMessages([]);
    }
  }, [detail, openDmWith]);

  // Deep-link: the activity feed sends users to
  // /student/competition/[id]/team?dm=<userId> when they tap a
  // "X messaged you" notification. When that param is present and we know
  // the team detail, auto-open that DM. Only fires once per param change
  // so user-toggling Back doesn't re-open it.
  const dmParam = Array.isArray(params.dm) ? params.dm[0] : params.dm;
  const dmDeepLinkAppliedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!dmParam || !detail || dmDeepLinkAppliedRef.current === dmParam) return;
    const member = detail.members.find((m) => m.student_id === dmParam);
    if (!member) return;
    dmDeepLinkAppliedRef.current = dmParam;
    void (async () => {
      const { threadId } = await openOrCreateDmThread(dmParam);
      setOpenDmWith({
        studentId: dmParam,
        name: member.full_name ?? dmParam.slice(0, 8),
        avatarUrl: member.avatar_url ?? null,
        threadId: threadId ?? null,
      });
    })();
  }, [dmParam, detail]);

  // Open or create a DM with a given teammate.
  const onOpenDmWith = useCallback(
    async (studentId: string, name: string, avatarUrl: string | null) => {
      // Optimistic: open the panel immediately with threadId=null while we
      // bootstrap the row server-side.
      setOpenDmWith({ studentId, name, avatarUrl, threadId: null });
      setDmMessages([]);
      const { threadId, error } = await openOrCreateDmThread(studentId);
      if (error || !threadId) {
        setOpenDmWith(null);
        Alert.alert("Could not open DM", error?.message ?? "Try again.");
        return;
      }
      setOpenDmWith((prev) => (prev && prev.studentId === studentId ? { ...prev, threadId } : prev));
    },
    []
  );

  const onCloseDm = useCallback(() => {
    setOpenDmWith(null);
    setDmMessages([]);
    setDmDraft("");
  }, []);

  const onSendDm = useCallback(async () => {
    if (!openDmWith?.threadId) return;
    const body = dmDraft.trim();
    if (!body) return;
    setDmSending(true);
    const { message, error } = await sendDmMessage(openDmWith.threadId, body, {
      replyToMessageId: dmReplyTo?.id ?? null,
    });
    setDmSending(false);
    if (error) {
      Alert.alert("Could not send", error.message);
      return;
    }
    setDmDraft("");
    setDmReplyTo(null);
    if (message) {
      setDmMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
      setTimeout(() => dmScrollRef.current?.scrollToEnd({ animated: true }), 50);
    }
  }, [openDmWith, dmDraft, dmReplyTo]);

  // DM hover toolbar handlers — reply just stages the parent locally;
  // delete soft-deletes via the SECURITY DEFINER RPC (sender-only).
  const onReplyDm = useCallback((m: DmMessage) => {
    setDmReplyTo(m);
  }, []);

  const onDeleteDm = useCallback(async (m: DmMessage) => {
    const optimisticAt = new Date().toISOString();
    setDmMessages((prev) =>
      prev.map((x) =>
        x.id === m.id
          ? { ...x, deleted_at: optimisticAt, deleted_by_user_id: myUserId ?? null, deleted_by_name: x.sender_name ?? null }
          : x
      )
    );
    const { ok, error } = await deleteDmMessage(m.id);
    if (!ok) {
      setDmMessages((prev) =>
        prev.map((x) =>
          x.id === m.id && x.deleted_at === optimisticAt
            ? { ...x, deleted_at: null, deleted_by_user_id: null, deleted_by_name: null }
            : x
        )
      );
      const msg = error?.message ?? "Try again.";
      if (typeof console !== "undefined") {
        console.error("[deleteDmMessage] failed:", msg);
      }
      if (Platform.OS === "web" && typeof window !== "undefined") {
        window.alert("Could not delete message: " + msg);
      } else {
        Alert.alert("Could not delete", msg);
      }
    }
  }, [myUserId]);

  // Snapshot the persisted last-read timestamp once on entry. We don't update
  // sessionUnreadCutoff after this — only the persisted value moves forward.
  useEffect(() => {
    if (!teamId) return;
    setUnreadAnchorY(null);
    setArrowDismissed(false);
    setCutoffLoaded(false);
    let alive = true;
    void (async () => {
      const iso = await getMyLastReadAt(teamId);
      if (alive) {
        setSessionUnreadCutoff(iso);
        setCutoffLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [teamId]);

  // First unread message id (or null if everything is read). Computed against
  // the SESSION cutoff so the divider doesn't vanish mid-session as soon as
  // the user reaches the bottom.
  //
  // Special case: if sessionUnreadCutoff is null (DB last_read_at is NULL —
  // user has never read this team), treat the cutoff as 0 so EVERY non-self
  // message counts as unread and the divider lands on the first one.
  // Without this, first-time visitors saw a non-zero unread badge but no
  // divider or arrow inside the chat.
  const firstUnreadId = useMemo(() => {
    // Don't compute against the cutoff until it has actually loaded —
    // otherwise the cached chat hydrates with cutoff=null defaulting to 0,
    // every message looks unread, and the jump-to-unread arrow flashes on
    // entry until getMyLastReadAt resolves a moment later.
    if (!cutoffLoaded) return null;
    const cutoff = sessionUnreadCutoff ? new Date(sessionUnreadCutoff).getTime() : 0;
    if (Number.isNaN(cutoff)) return null;
    for (const m of messages) {
      if (m.sender_id === myUserId) continue;
      if (new Date(m.created_at).getTime() > cutoff) return m.id;
    }
    return null;
  }, [messages, sessionUnreadCutoff, myUserId, cutoffLoaded]);

  // Persist "everything is read" once the user is at the bottom of the chat.
  // We only write to storage; we never update sessionUnreadCutoff, so the
  // divider stays where it is for the rest of this visit. Next time the user
  // opens the team tab, sessionUnreadCutoff will be re-snapped from the new
  // persisted value and the divider naturally disappears.
  useEffect(() => {
    if (!teamId || !chatScrolledToBottom || messages.length === 0) return;
    const newest = messages[messages.length - 1];
    const newestIso = newest?.created_at ?? null;
    if (!newestIso) return;
    void markTeamRead(teamId, newestIso);
  }, [teamId, chatScrolledToBottom, messages]);

  // The "Jump to unread" arrow only renders when:
  //   - there is in fact an unread message, AND
  //   - the unread divider's anchor is above the current scroll viewport
  //     (so the user can't already see it).
  const showJumpToUnread =
    !arrowDismissed &&
    firstUnreadId != null &&
    unreadAnchorY != null &&
    chatScrollY > unreadAnchorY + 8;

  // Triggered by the arrow: scroll the chat fast to the divider, AND persist
  // the latest message as read (we don't change sessionUnreadCutoff so the
  // divider stays visible — but on next entry it'll be gone). Also flag
  // arrowDismissed so the chip doesn't reappear if the user scrolls back up
  // past the divider during this same visit.
  const onJumpToUnread = useCallback(() => {
    setArrowDismissed(true);
    if (unreadAnchorY == null || !chatScrollRef.current) return;
    chatScrollRef.current.scrollTo({
      y: Math.max(0, unreadAnchorY - 12),
      animated: true,
    });
    if (teamId && messages.length > 0) {
      const newest = messages[messages.length - 1];
      if (newest?.created_at) void markTeamRead(teamId, newest.created_at);
    }
  }, [teamId, messages, unreadAnchorY]);

  const onSend = useCallback(async () => {
    if (!detail) return;
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    const { message, error } = await sendTeamMessage(detail.team.id, body, {
      replyToMessageId: replyTo?.id ?? null,
    });
    setSending(false);
    if (error) {
      Alert.alert("Could not send", error.message);
      return;
    }
    setDraft("");
    setReplyTo(null);
    if (message) {
      // Optimistic dedupe: realtime will likely also fire
      setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
      setTimeout(() => chatScrollRef.current?.scrollToEnd({ animated: true }), 50);
    }
  }, [detail, draft, replyTo]);

  // Handlers for the message-row hover actions. Pin/unpin and delete go
  // through the SECURITY DEFINER RPCs (which enforce membership/captain
  // checks server-side); reply just sets local state and the composer
  // picks it up.
  const onReplyToMessage = useCallback((m: TeamMessage) => {
    setReplyTo(m);
  }, []);

  const onTogglePinMessage = useCallback(async (m: TeamMessage) => {
    const isPinned = pinned.some((p) => p.message_id === m.id);
    const { ok, error } = isPinned
      ? await unpinTeamMessage(m.id)
      : await pinTeamMessage(m.id);
    if (!ok) {
      Alert.alert(isPinned ? "Could not unpin" : "Could not pin", error?.message ?? "Try again.");
      return;
    }
    // Optimistic refresh; realtime will reconcile.
    if (detail) {
      const pins = await listPinnedMessages(detail.team.id);
      setPinned(pins);
    }
  }, [pinned, detail]);

  // Hover-toolbar delete on a regular team chat message. The user already
  // clicked the trash icon — that's intentional enough; no extra confirm.
  // We flip the row to "deleted" locally first so the click feels instant,
  // then the SECURITY DEFINER RPC nukes the body server-side. If that
  // fails we restore + surface the error (RN Alert.alert is a no-op on
  // web, so we use window.alert + console.error explicitly).
  const onDeleteMessage = useCallback(async (m: TeamMessage) => {
    const optimisticAt = new Date().toISOString();
    setMessages((prev) =>
      prev.map((x) =>
        x.id === m.id
          ? {
              ...x,
              deleted_at: optimisticAt,
              deleted_by_user_id: myUserId ?? null,
              // Seeded so the ghost row says "Message deleted by you"
              // straight away; the realtime UPDATE will overwrite this
              // with the actual server-side row anyway.
              deleted_by_name: x.sender_id === myUserId ? x.sender_name : null,
            }
          : x
      )
    );
    setPinned((prev) => prev.filter((p) => p.message_id !== m.id));
    const { ok, error } = await deleteTeamMessage(m.id);
    if (!ok) {
      // Roll back the optimistic flip.
      setMessages((prev) =>
        prev.map((x) =>
          x.id === m.id && x.deleted_at === optimisticAt
            ? { ...x, deleted_at: null, deleted_by_user_id: null, deleted_by_name: null }
            : x
        )
      );
      const msg = error?.message ?? "Try again.";
      if (typeof console !== "undefined") {
        console.error("[deleteTeamMessage] failed:", msg);
      }
      if (Platform.OS === "web" && typeof window !== "undefined") {
        window.alert("Could not delete message: " + msg);
      } else {
        Alert.alert("Could not delete", msg);
      }
    }
  }, [myUserId]);

  // Click on a pinned chip: scroll its message into view + briefly highlight.
  const onJumpToPinned = useCallback((messageId: string) => {
    setHighlightMessageId(messageId);
    setTimeout(() => setHighlightMessageId(null), 1800);
    // Best-effort scroll: depends on row layout being measured. The chat
    // ScrollView doesn't have per-row anchors yet, so we scroll to top
    // (older messages) as a hint. A precise scroll would need per-row
    // onLayout y coords keyed by message id — left as a follow-up.
    chatScrollRef.current?.scrollTo({ y: 0, animated: true });
  }, []);

  // Captain-only — hard-deletes a competition-scoped announcement via the
  // SECURITY DEFINER RPC. Removing the row makes it disappear from team
  // chat, the program-scoped overlay, and the activity bell at once.
  // No confirm dialog: the click was explicit and the action is recoverable
  // (resend a duplicate). Errors surface via window.alert on web so we
  // never silently swallow a failure (RN Alert.alert is a no-op on web).
  const onDeleteAnnouncement = useCallback(async (a: MyAnnouncement) => {
    // Optimistic — yank from local state immediately so the click feels
    // instant. If the RPC fails we'll show the error and re-add below.
    setAnnouncements((prev) => prev.filter((x) => x.id !== a.id));
    try {
      await captainDeleteAnnouncement(a.id);
    } catch (err) {
      // Restore so the user doesn't think it succeeded.
      setAnnouncements((prev) => (prev.some((x) => x.id === a.id) ? prev : [...prev, a]));
      const msg = err instanceof Error ? err.message : "Try again.";
      if (typeof console !== "undefined") {
        console.error("[captainDeleteAnnouncement] failed:", msg);
      }
      if (Platform.OS === "web" && typeof window !== "undefined") {
        window.alert("Could not delete announcement: " + msg);
      } else {
        Alert.alert("Could not delete", msg);
      }
    }
  }, []);

  const onToggleVisibility = useCallback(async () => {
    if (!detail || !detail.is_captain) return;
    setBusy(true);
    const next = detail.team.visibility === "public" ? "private" : "public";
    const { error } = await setTeamVisibility(detail.team.id, next);
    setBusy(false);
    if (error) {
      Alert.alert("Could not update", error.message);
      return;
    }
    await reload();
  }, [detail, reload]);

  const onToggleLock = useCallback(async () => {
    if (!detail || !detail.is_captain) return;
    setBusy(true);
    const { error } = await lockTeam(detail.team.id, !detail.team.is_locked);
    setBusy(false);
    if (error) {
      Alert.alert("Could not update", error.message);
      return;
    }
    await reload();
  }, [detail, reload]);

  const onLeaveConfirmed = useCallback(async () => {
    if (!detail) return;
    setBusy(true);
    const { error } = await leaveTeam(detail.team.id);
    setBusy(false);
    setConfirmingLeave(false);
    if (error) {
      Alert.alert("Could not leave", error.message);
      return;
    }
    // Drop the myTeam cache for this competition so the next mount of the
    // workspace correctly shows the "Find Team" tab without a stale flash.
    // Also drop the team-detail cache for this team — it would otherwise
    // hydrate stale "you're a member" data the next time we land here.
    if (myUserId && detail.competition.id) {
      clearCachedMyTeam(myUserId, detail.competition.id);
    }
    if (myUserId) {
      clearCachedTeamDetail(myUserId, detail.team.id);
      clearCachedTeamChat(myUserId, detail.team.id);
    }
    router.replace("/student");
  }, [detail, myUserId, router]);

  const onDeleteConfirmed = useCallback(async () => {
    if (!detail || !detail.is_captain) return;
    setBusy(true);
    const { error } = await deleteTeam(detail.team.id);
    setBusy(false);
    setConfirmingDelete(false);
    if (error) {
      Alert.alert("Could not delete", error.message);
      return;
    }
    if (myUserId && detail.competition.id) {
      clearCachedMyTeam(myUserId, detail.competition.id);
    }
    if (myUserId) {
      clearCachedTeamDetail(myUserId, detail.team.id);
      clearCachedTeamChat(myUserId, detail.team.id);
    }
    router.replace("/student");
  }, [detail, myUserId, router]);

  // Two-step kick: tapping the roster row's remove icon opens an inline
  // destructive confirm card under the roster, and only a follow-up tap
  // actually removes the member. Kicking is irreversible (the kicked user
  // has to be re-invited via link or join the team again as public), so
  // the user has to read what they're doing.
  const onRequestKick = useCallback(
    (studentId: string, name: string) => {
      setConfirmingKick({ studentId, name });
    },
    []
  );

  const onKickConfirmed = useCallback(async () => {
    if (!detail || !detail.is_captain || !confirmingKick) return;
    setBusy(true);
    const { error } = await kickMember(detail.team.id, confirmingKick.studentId);
    setBusy(false);
    setConfirmingKick(null);
    if (error) {
      Alert.alert("Could not remove", error.message);
      return;
    }
    await reload();
  }, [detail, confirmingKick, reload]);

  // Two-step promote: tapping "Make captain" opens an inline disclaimer, and only
  // a follow-up confirm actually flips the bit. Captain rights are full mutual
  // power so we want the user to read what they are giving away.
  const onRequestPromote = useCallback(
    (studentId: string, name: string) => {
      setConfirmingPromote({ studentId, name });
    },
    []
  );

  const onPromoteConfirmed = useCallback(async () => {
    if (!detail || !detail.is_captain || !confirmingPromote) return;
    setBusy(true);
    const { error } = await promoteTeamMemberToCaptain(
      detail.team.id,
      confirmingPromote.studentId
    );
    setBusy(false);
    setConfirmingPromote(null);
    if (error) {
      Alert.alert("Could not promote", error.message);
      return;
    }
    await reload();
  }, [detail, confirmingPromote, reload]);

  const onAcceptRequest = useCallback(
    async (requestId: string) => {
      setBusy(true);
      const { error } = await acceptJoinRequest(requestId);
      setBusy(false);
      if (error) {
        Alert.alert("Could not accept", error.message);
        return;
      }
      await reload();
    },
    [reload]
  );

  const onDeclineRequest = useCallback(
    async (requestId: string) => {
      setBusy(true);
      // Note: previously used cancelJoinRequest, which only updates rows
      // where student_id = me — captains aren't the requester so it
      // silently no-op'd. declineJoinRequest goes through a SECURITY
      // DEFINER RPC that verifies captain status server-side.
      const { error } = await declineJoinRequest(requestId);
      setBusy(false);
      if (error) {
        Alert.alert("Could not decline", error.message);
        return;
      }
      await reload();
    },
    [reload]
  );

  const onIgnoreRequest = useCallback(
    async (requestId: string) => {
      setBusy(true);
      const { error } = await ignoreTeamRequester(requestId);
      setBusy(false);
      if (error) {
        Alert.alert("Could not ignore", error.message);
        return;
      }
      await reload();
    },
    [reload]
  );

  const status = useMemo(
    () => (detail ? compStatusLabel(detail.competition.status) : { label: "—", tint: "muted" as const }),
    [detail]
  );

  // Team-lookup spinner — shown only on a true cold start with no cached
  // team for this competition. Cache hits skip straight through. The
  // surrounding shell is rendered by _layout.tsx; this just fills the body.
  if (teamLookupLoading && !teamId) {
    return (
      <View className="flex-1 items-center justify-center">
        <ActivityIndicator size="large" color={COLORS.FOREST.hex} />
      </View>
    );
  }

  // Student isn't on a team for this competition yet — empty state with a
  // CTA back to the workspace's inline Find Team view.
  if (noTeamYet) {
    return (
      <View className="flex-1 items-center justify-center px-6">
        <View style={{ maxWidth: 480, alignItems: "center" }}>
          <Text
            style={{
              fontFamily: FONTS.display,
              fontSize: 32,
              lineHeight: 36,
              color: COLORS.FOREST.hex,
              marginBottom: 10,
              textAlign: "center",
            }}
          >
            You&apos;re not on a team yet.
          </Text>
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 14,
              lineHeight: 22,
              color: STUDENT_THEME.textOnSurfaceDim,
              textAlign: "center",
              marginBottom: 18,
            }}
          >
            Join an existing team or create your own to compete.
          </Text>
          <Button
            variant="primary"
            onPress={() =>
              router.push(`/student/competition/${competitionId}` as Href)
            }
          >
            BROWSE TEAMS
          </Button>
        </View>
      </View>
    );
  }

  return (
    <>
      {/* Fixed-viewport layout: the page itself does not scroll. The chat
          and roster each scroll inside their own panels so the composer and
          the "+ Add member" stay pinned to the bottom of their columns. */}
      <View className="flex-1" style={{ minHeight: 0 }}>
        {loading ? (
          <View className="flex-1 items-center justify-center">
            <ActivityIndicator size="large" color={COLORS.FOREST.hex} />
          </View>
        ) : errorText || !detail ? (
          <View className="flex-1 items-center justify-center px-5 md:px-11">
            <EmptyState
              title="Team not available"
              subtitle={errorText ?? "You may not be a member of this team."}
            />
          </View>
        ) : (
          <View className="flex-1 flex-col" style={{ minHeight: 0 }}>
            {/* Compact hero — single horizontal row that mirrors the
                dashboard's green band height. Title + status pills sit
                inline on the left, captain control cluster on the right. */}
            <View
              className="px-4 py-3 md:px-6"
              style={{
                backgroundColor: COLORS.FOREST.hex,
                borderBottomWidth: 1,
                borderBottomColor: STUDENT_THEME.sidebarItemActiveBg,
                // Lift the hero so the captain control cluster's hover tooltips
                // always paint above the rounded chat / roster panels below.
                zIndex: 50,
                position: "relative",
              }}
            >
              <View className="flex-row flex-wrap items-center justify-between gap-x-5 gap-y-2">
                <View
                  className="min-w-0 flex-col"
                  style={{ flexShrink: 1, minWidth: 0, flex: 1, gap: 4 }}
                >
                  {/* Program kicker — small linen-tinted overline that places
                      the team in the context of its parent competition. */}
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 9.5,
                      letterSpacing: 1.9,
                      textTransform: "uppercase",
                      color: "rgba(247,244,238,0.55)",
                    }}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  >
                    {detail.competition.name}
                  </Text>
                  <View
                    className="flex-row flex-wrap items-center"
                    style={{ gap: 12 }}
                  >
                    <Text
                      style={{
                        fontFamily: FONTS.displaySemiBold,
                        color: COLORS.AMBER_WARM.hex,
                        fontSize: 26,
                        lineHeight: 30,
                        letterSpacing: -0.13,
                      }}
                      numberOfLines={1}
                      ellipsizeMode="tail"
                    >
                      {detail.team.name}
                    </Text>
                    {/* QUALIFIED pill — render only while the comp is in
                        active judging or has closed past it (i.e., once
                        teams have actually been graded). Suppressed during
                        registration phases to avoid a misleading badge. */}
                    {detail.competition.status === "judging" ||
                    detail.competition.status === "closed" ? (
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 5,
                          paddingHorizontal: 9,
                          paddingVertical: 3,
                          borderRadius: 999,
                          borderWidth: 1,
                          borderColor: STUDENT_THEME.accentBorderHover,
                          backgroundColor: STUDENT_THEME.accentTintSoft,
                        }}
                      >
                        <Svg width={9} height={9} viewBox="0 0 12 12" fill="none">
                          <Path
                            d="M2.5 6l2.4 2.4L9.5 3.6"
                            stroke={COLORS.AMBER.hex}
                            strokeWidth={2.4}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </Svg>
                        <Text
                          style={{
                            fontFamily: FONTS.bodyBold,
                            fontSize: 9,
                            letterSpacing: 1.5,
                            color: COLORS.AMBER.hex,
                            textTransform: "uppercase",
                          }}
                        >
                          Qualified
                        </Text>
                      </View>
                    ) : null}
                  </View>
                </View>
                {/* Captain control cluster — flex-shrink-0 so the icons keep
                    their natural size and the title takes any extra room. */}
                <View
                  className="flex-row flex-wrap items-center justify-end gap-1.5"
                  style={{ flexShrink: 0 }}
                >
                  {detail.is_captain ? (
                    <>
                      <TeamIconButton
                        label={detail.team.visibility === "public" ? "Make private" : "Make public"}
                        description={
                          detail.team.visibility === "public"
                            ? "Hide your team from the public team browser. Only people you invite or who request to join can find it."
                            : "List your team in the public browser so any eligible student can join instantly."
                        }
                        active={detail.team.visibility === "private"}
                        disabled={busy}
                        onPress={onToggleVisibility}
                        icon={(c) => <EyeIcon hidden={detail.team.visibility === "private"} color={c} />}
                      />
                      <TeamIconButton
                        label={detail.team.is_locked ? "Unlock team" : "Lock team"}
                        description={
                          detail.team.is_locked
                            ? "Reopen the team so new members can join or be accepted again."
                            : "Freeze the roster: no new joins, requests, or accepting invites — even by you. Use when your team is set."
                        }
                        active={Boolean(detail.team.is_locked)}
                        disabled={busy}
                        onPress={onToggleLock}
                        icon={(c) => <Padlock closed={Boolean(detail.team.is_locked)} color={c} />}
                      />
                      <TeamIconButton
                        label="Edit pitch"
                        description="Update the team description and what you're looking for. This is what other students see when browsing teams in the case."
                        active={pitchOverlayOpen}
                        disabled={busy}
                        onPress={() => setPitchOverlayOpen(true)}
                        icon={(c) => <PencilGlyph color={c} />}
                      />
                    </>
                  ) : null}
                  {detail.is_member ? (
                    <TeamIconButton
                      label="Leave team"
                      description={
                        detail.is_owner && detail.members.length > 1
                          ? "Step away from this team. Ownership transfers to the next member so the team keeps competing."
                          : detail.is_owner && detail.members.length === 1
                            ? "You're the only member, so leaving will dissolve the team."
                            : "Step away from this team. The captain will be notified."
                      }
                      disabled={busy || (detail.is_owner && detail.members.length === 1)}
                      onPress={() => setConfirmingLeave(true)}
                      icon={(c) => <LeaveIcon color={c} />}
                    />
                  ) : null}
                  {detail.is_captain ? (
                    <TeamIconButton
                      label="Delete team"
                      description="Permanently remove this team. Members will need to join or create a new one to keep competing."
                      destructive
                      disabled={busy}
                      onPress={() => setConfirmingDelete(true)}
                      icon={(c) => <TrashIcon color={c} />}
                    />
                  ) : null}
                </View>
              </View>
            </View>

            {/* Confirmation panels (leave / delete) — anchored just below the
                hero so they can't be missed. They take their natural height
                and the body fills the rest. */}
            {confirmingLeave ? (
              <View className="px-4 pt-3 md:px-6">
                <ConfirmInline
                  prompt={
                    detail.is_owner && detail.members.length > 1
                      ? "Leave this team? Ownership will transfer to the next member."
                      : "Leave this team?"
                  }
                  confirmLabel="Confirm leave"
                  onCancel={() => setConfirmingLeave(false)}
                  onConfirm={() => void onLeaveConfirmed()}
                  busy={busy}
                  destructive
                />
              </View>
            ) : null}
            {confirmingDelete ? (
              <View className="px-4 pt-3 md:px-6">
                <ConfirmInline
                  prompt="Delete this team? This can't be undone."
                  confirmLabel="Confirm delete"
                  onCancel={() => setConfirmingDelete(false)}
                  onConfirm={() => void onDeleteConfirmed()}
                  busy={busy}
                  destructive
                />
              </View>
            ) : null}

            {/* Body — fills the remaining viewport. Two columns on md+ live
                inside a sand-coloured plate with real gutters so the chat and
                roster panels read as cards floating above the page rather
                than flush rectangles glued to every edge. */}
            <View
              className="flex-1 flex-col gap-4 px-4 pt-4 pb-4 md:flex-row md:gap-5 md:px-6 md:pt-5 md:pb-5"
              style={{ minHeight: 0 }}
            >
              {/* LEFT — chat panel (rounded card). */}
              <View
                className="flex-1 flex-col min-w-0 overflow-hidden rounded-[16px] border border-sand-dark bg-white"
                style={{ minHeight: 0 }}
              >
                {/* Pending join requests — captain-only banner above the chat. */}
                {detail.is_captain && pending.length > 0 ? (
                  <View
                    className="border-b border-sand-dark px-4 py-3 md:px-6"
                    style={{ backgroundColor: "rgba(181,135,58,0.05)", flexShrink: 0 }}
                  >
                    <Text
                      className="text-[10px] font-bold uppercase tracking-widest-3"
                      style={{ fontFamily: FONTS.bodyBold, color: COLORS.AMBER.hex, letterSpacing: 1.8 }}
                    >
                      {`Pending requests · ${pending.length}`}
                    </Text>
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator={false}
                      contentContainerStyle={{ gap: 10, paddingTop: 8 }}
                    >
                      {pending.map((p) => {
                        const displayName = p.full_name ?? p.student_id.slice(0, 8);
                        return (
                          <View
                            key={p.id}
                            className="flex-row items-center gap-2 rounded-md border border-sand-dark bg-white px-3 py-2"
                          >
                            {/* Avatar + name are a single click target that
                                opens the requester's profile so the captain
                                can decide whether to accept. */}
                            <Pressable
                              onPress={() => setPeerProfileFor(p.student_id)}
                              accessibilityRole="button"
                              accessibilityLabel={`View profile: ${displayName}`}
                              style={({ hovered }: { hovered?: boolean }) => [
                                {
                                  flexDirection: "row",
                                  alignItems: "center",
                                  gap: 8,
                                  paddingRight: 4,
                                  borderRadius: 4,
                                  backgroundColor:
                                    hovered && Platform.OS === "web"
                                      ? "rgba(181,135,58,0.06)"
                                      : "transparent",
                                },
                                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                              ]}
                            >
                              <Avatar
                                name={displayName}
                                imageUrl={p.avatar_url}
                                size="sm"
                              />
                              <Text
                                className="text-[12.5px]"
                                style={{ fontFamily: FONTS.bodySemiBold, color: COLORS.FOREST.hex }}
                              >
                                {displayName}
                              </Text>
                            </Pressable>
                            <Pressable
                              onPress={() => void onDeclineRequest(p.id)}
                              disabled={busy}
                              style={pillWeb}
                            >
                              <Text
                                className="text-[10.5px] uppercase"
                                style={{ fontFamily: FONTS.bodyMedium, color: STUDENT_THEME.textOnSurfaceDim, letterSpacing: 1.2 }}
                              >
                                Decline
                              </Text>
                            </Pressable>
                            {/* Ignore: declines AND blocks future requests
                                from this student to this team. The captain
                                can lift the block from Settings. */}
                            <Pressable
                              onPress={() => void onIgnoreRequest(p.id)}
                              disabled={busy}
                              style={pillWeb}
                              accessibilityLabel="Decline and block future requests"
                            >
                              <Text
                                className="text-[10.5px] uppercase"
                                style={{ fontFamily: FONTS.bodyBold, color: "#A33D2F", letterSpacing: 1.2 }}
                              >
                                Ignore
                              </Text>
                            </Pressable>
                            <Button size="sm" variant="primary" onPress={() => void onAcceptRequest(p.id)} disabled={busy}>
                              Accept
                            </Button>
                          </View>
                        );
                      })}
                    </ScrollView>
                  </View>
                ) : null}

                {/* Chat header — fixed height. The Team Chat label sits on
                    the left; for captains, a small megaphone icon sits in
                    the right corner that opens MegaphoneCompose scoped to
                    this competition. Hidden for non-captains. */}
                <View
                  className="border-b px-4 pt-3 pb-3 md:px-6"
                  style={{
                    flexShrink: 0,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: STUDENT_THEME.surfaceRaisedBg,
                    borderBottomColor: COLORS.SAND_LINE.hex,
                  }}
                >
                  <Text
                    className="font-bold uppercase"
                    style={{
                      fontFamily: FONTS.bodyBold,
                      color: COLORS.AMBER_DEEP.hex,
                      letterSpacing: 1.98,
                      fontSize: 11,
                    }}
                  >
                    Team Chat
                  </Text>
                  {detail?.is_captain && detail.competition?.id ? (
                    <Pressable
                      onPress={() => setMegaphoneOpen(true)}
                      accessibilityRole="button"
                      accessibilityLabel="Send announcement"
                      style={({ hovered }: { hovered?: boolean }) => [
                        {
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 7,
                          paddingHorizontal: 10,
                          paddingVertical: 5,
                          borderRadius: 4,
                          backgroundColor:
                            hovered && Platform.OS === "web"
                              ? "rgba(100,127,77,0.18)"
                              : STUDENT_THEME.successTintSoft,
                          borderWidth: 1,
                          borderColor: STUDENT_THEME.successBorderSoft,
                        },
                        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                      ]}
                    >
                      <Text
                        style={{
                          fontFamily: FONTS.bodyBold,
                          fontSize: 10.5,
                          letterSpacing: 1.4,
                          textTransform: "uppercase",
                          color: "#3F5A30",
                        }}
                      >
                        Announce
                      </Text>
                      {/* Handheld megaphone / bullhorn — wide cone on the
                          right with a stubby grip on the bottom-left and
                          three short sound waves emitting forward. */}
                      <Svg width={15} height={15} viewBox="0 0 24 24" fill="none">
                        {/* Cone body */}
                        <Path
                          d="M4 9 L4 15 L11 15 L19 19 L19 5 L11 9 Z"
                          fill={COLORS.SAGE_DEEP.hex}
                          stroke={"#3F5A30"}
                          strokeWidth={1.2}
                          strokeLinejoin="round"
                        />
                        {/* Handle / grip on the underside */}
                        <Path
                          d="M7 15 L7 19 L9 19 L9 15"
                          fill={"#3F5A30"}
                          stroke={"#3F5A30"}
                          strokeWidth={0.8}
                          strokeLinejoin="round"
                        />
                        {/* Sound waves emitting forward (right) */}
                        <Path
                          d="M21 9 Q22.6 12 21 15"
                          stroke={COLORS.SAGE_DEEP.hex}
                          strokeWidth={1.4}
                          fill="none"
                          strokeLinecap="round"
                        />
                      </Svg>
                    </Pressable>
                  ) : null}
                </View>

                {/* Pinned-messages banner — only renders when the team has
                    one or more pins. Plum-tinted, collapses to top pin by
                    default; "View all" expands. Click any pin to jump. */}
                {pinned.length > 0 ? (
                  <PinnedBanner
                    pinned={pinned}
                    expanded={pinsExpanded}
                    onToggleExpand={() => setPinsExpanded((v) => !v)}
                    onJump={onJumpToPinned}
                    canUnpin={!!detail?.is_captain}
                    onUnpin={async (messageId) => {
                      const { ok, error } = await unpinTeamMessage(messageId);
                      if (!ok) {
                        Alert.alert("Could not unpin", error?.message ?? "Try again.");
                        return;
                      }
                      if (detail) setPinned(await listPinnedMessages(detail.team.id));
                    }}
                  />
                ) : null}

                {/* Chat messages — scrolls inside this column. */}
                <View className="flex-1" style={{ minHeight: 0, position: "relative" }}>
                  <ScrollView
                    ref={chatScrollRef}
                    className="flex-1 bg-white"
                    style={{ minHeight: 0 }}
                    contentContainerStyle={{ padding: 16, gap: 12 }}
                    onScroll={(e) => {
                      const { layoutMeasurement, contentOffset, contentSize } = e.nativeEvent;
                      const distanceFromBottom =
                        contentSize.height - (contentOffset.y + layoutMeasurement.height);
                      setChatScrolledToBottom(distanceFromBottom < 80);
                      setChatScrollY(contentOffset.y);
                    }}
                    scrollEventThrottle={64}
                    onContentSizeChange={() => {
                      // Once the actual content height is known, snap to the
                      // bottom — but only the first time, so the user isn't
                      // yanked back down later when new messages arrive while
                      // they're reading something further up.
                      if (!initialScrollDoneRef.current) {
                        chatScrollRef.current?.scrollToEnd({ animated: false });
                        // Mark done on the next frame to swallow any duplicate
                        // size-change events from the same layout pass.
                        setTimeout(() => {
                          initialScrollDoneRef.current = true;
                        }, 60);
                      }
                    }}
                  >
                    {chatLoading ? (
                      <View className="items-center py-12">
                        <ActivityIndicator size="small" color={COLORS.FOREST.hex} />
                      </View>
                    ) : messages.length === 0 && events.length === 0 ? (
                      <View className="items-center py-16">
                        <Text
                          className="text-[13px]"
                          style={{ fontFamily: FONTS.body, color: STUDENT_THEME.textOnSurfaceFaint }}
                        >
                          No messages yet — say hello.
                        </Text>
                      </View>
                    ) : (
                      (() => {
                        // Pre-group consecutive same-sender messages, then
                        // merge those groups with team_events into a single
                        // chronological timeline so events render inline as
                        // grey system rows between message bubbles.
                        type Group = { id: string; sender_id: string; messages: TeamMessage[] };
                        const groups: Group[] = [];
                        for (const m of messages) {
                          const last = groups[groups.length - 1];
                          const lastMsg = last ? last.messages[last.messages.length - 1] : null;
                          const closeInTime =
                            lastMsg != null &&
                            new Date(m.created_at).getTime() -
                              new Date(lastMsg.created_at).getTime() <
                              5 * 60 * 1000;
                          if (last && last.sender_id === m.sender_id && closeInTime) {
                            last.messages.push(m);
                          } else {
                            groups.push({ id: m.id, sender_id: m.sender_id, messages: [m] });
                          }
                        }

                        type Item =
                          | { kind: "group"; at: string; group: Group }
                          | { kind: "event"; at: string; event: TeamEvent }
                          | { kind: "announcement"; at: string; announcement: MyAnnouncement };
                        const items: Item[] = [
                          ...groups.map<Item>((g) => ({
                            kind: "group",
                            at: g.messages[0].created_at,
                            group: g,
                          })),
                          ...events.map<Item>((e) => ({
                            kind: "event",
                            at: e.created_at,
                            event: e,
                          })),
                          ...announcements.map<Item>((a) => ({
                            kind: "announcement",
                            at: a.published_at ?? a.created_at,
                            announcement: a,
                          })),
                        ].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

                        return items.map((it) => {
                          if (it.kind === "event") {
                            return <TeamEventRow key={`e:${it.event.id}`} event={it.event} myUserId={myUserId} />;
                          }
                          if (it.kind === "announcement") {
                            return (
                              <AnnouncementChatEvent
                                key={`a:${it.announcement.id}`}
                                announcement={it.announcement}
                                authorName={null}
                                onPressSeeMore={() => setAnnouncementsOverlayOpen(true)}
                                canDelete={!!detail?.is_captain}
                                onDelete={onDeleteAnnouncement}
                              />
                            );
                          }
                          const g = it.group;
                          const showDivider =
                            firstUnreadId != null && g.messages[0].id === firstUnreadId;
                          return (
                            <View
                              key={`g:${g.id}`}
                              onLayout={
                                showDivider
                                  ? (e) => {
                                      const y = e.nativeEvent.layout.y;
                                      setUnreadAnchorY((prev) => (prev === y ? prev : y));
                                    }
                                  : undefined
                              }
                            >
                              {showDivider ? <UnreadDivider /> : null}
                              <ChatMessageGroup
                                messages={g.messages}
                                isMine={myUserId === g.sender_id}
                                myUserId={myUserId}
                                isCaptain={!!detail?.is_captain}
                                pinnedIds={new Set(pinned.map((p) => p.message_id))}
                                highlightId={highlightMessageId}
                                onPressLink={(url, sender) => setLinkConfirm({ url, sender })}
                                onPressSender={(senderId) => setPeerProfileFor(senderId)}
                                onReply={onReplyToMessage}
                                onTogglePin={onTogglePinMessage}
                                onDelete={onDeleteMessage}
                              />
                            </View>
                          );
                        });
                      })()
                    )}
                  </ScrollView>

                  <LinkWarningModal state={linkConfirm} onClose={() => setLinkConfirm(null)} />

                  {/* Jump-to-unread floating button. Only renders when the
                      unread divider is currently above the user's viewport
                      (so they actually need to scroll up to see it). */}
                  {showJumpToUnread ? (
                    <Pressable
                      onPress={onJumpToUnread}
                      style={[
                        {
                          // Top-right of the chat panel — the unread divider
                          // is ABOVE the user's current view, so the arrow
                          // points up and lives near the ceiling of the chat.
                          position: "absolute",
                          top: 12,
                          right: 18,
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 8,
                          paddingHorizontal: 14,
                          paddingVertical: 9,
                          borderRadius: 999,
                          backgroundColor: COLORS.FOREST.hex,
                          borderWidth: 1,
                          borderColor: "rgba(181,135,58,0.55)",
                          zIndex: 5,
                        },
                        Platform.OS === "web"
                          ? ({
                              cursor: "pointer",
                              boxShadow: "0 12px 24px -10px rgba(0,0,0,0.45)",
                            } as unknown as ViewStyle)
                          : null,
                      ]}
                    >
                      <Svg width={11} height={11} viewBox="0 0 12 12" fill="none">
                        <Path
                          d="M6 10V2M2.5 5.5L6 2l3.5 3.5"
                          stroke={COLORS.AMBER.hex}
                          strokeWidth={1.6}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </Svg>
                      <Text
                        className="text-[11px] uppercase"
                        style={{
                          fontFamily: FONTS.bodyBold,
                          color: COLORS.AMBER.hex,
                          letterSpacing: 1.4,
                        }}
                      >
                        Jump to unread
                      </Text>
                    </Pressable>
                  ) : null}

                  {/* Scroll-to-bottom — small chevron pill in the corner.
                      Shows whenever the user has scrolled away from the
                      newest message and the Jump-to-unread arrow isn't
                      already there to handle it. */}
                  {!chatScrolledToBottom && !showJumpToUnread ? (
                    <Pressable
                      onPress={() =>
                        chatScrollRef.current?.scrollToEnd({ animated: true })
                      }
                      accessibilityLabel="Scroll to latest message"
                      style={[
                        {
                          position: "absolute",
                          bottom: 12,
                          right: 18,
                          width: 32,
                          height: 32,
                          borderRadius: 999,
                          backgroundColor: STUDENT_THEME.surfaceRaisedBg,
                          borderWidth: 1,
                          borderColor: "rgba(28,43,26,0.18)",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 5,
                        },
                        Platform.OS === "web"
                          ? ({
                              cursor: "pointer",
                              boxShadow: "0 8px 18px -10px rgba(0,0,0,0.35)",
                            } as unknown as ViewStyle)
                          : null,
                      ]}
                    >
                      <Svg width={12} height={12} viewBox="0 0 12 12" fill="none">
                        <Path
                          d="M2.5 4 L6 7.5 L9.5 4"
                          stroke={COLORS.FOREST.hex}
                          strokeWidth={1.6}
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </Svg>
                    </Pressable>
                  ) : null}
                </View>

                {/* Composer — pinned to the bottom of the chat column.
                    Outer wrapper provides the divider hairline and outer
                    padding; the textarea + send button sit inside a sand
                    rounded inner box per the design reference. */}
                <View
                  className="flex-col gap-2 border-t px-4 py-3"
                  style={{
                    backgroundColor: STUDENT_THEME.surfaceRaisedBg,
                    borderTopColor: COLORS.SAND_LINE.hex,
                    flexShrink: 0,
                  }}
                >
                  {/* Reply preview pill — shows when the user has tapped Reply
                      on a message. ✕ clears it; sending consumes it. */}
                  {replyTo ? (
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 8,
                        paddingHorizontal: 10,
                        paddingVertical: 6,
                        marginBottom: 6,
                        borderLeftWidth: 2,
                        borderLeftColor: COLORS.AMBER.hex,
                        backgroundColor: "rgba(181,135,58,0.08)",
                        borderRadius: 4,
                      }}
                    >
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text
                          style={{
                            fontFamily: FONTS.bodyBold,
                            fontSize: 10.5,
                            letterSpacing: 1.2,
                            color: COLORS.AMBER.hex,
                          }}
                        >
                          REPLYING TO {(replyTo.sender_name ?? "MEMBER").toUpperCase()}
                        </Text>
                        <Text
                          style={{
                            fontFamily: FONTS.body,
                            fontSize: 12,
                            color: STUDENT_THEME.textOnSurfaceDim,
                          }}
                          numberOfLines={1}
                        >
                          {replyTo.body}
                        </Text>
                      </View>
                      <Pressable
                        onPress={() => setReplyTo(null)}
                        accessibilityRole="button"
                        accessibilityLabel="Cancel reply"
                        style={[
                          {
                            width: 22,
                            height: 22,
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 4,
                          },
                          Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                        ]}
                      >
                        <Svg width={10} height={10} viewBox="0 0 12 12" fill="none">
                          <Path
                            d="M2 2 L10 10 M10 2 L2 10"
                            stroke={STUDENT_THEME.textOnSurfaceDim}
                            strokeWidth={1.4}
                            strokeLinecap="round"
                          />
                        </Svg>
                      </Pressable>
                    </View>
                  ) : null}

                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "flex-end",
                      gap: 10,
                      backgroundColor: COLORS.SAND.hex,
                      borderRadius: 10,
                      paddingHorizontal: 12,
                      paddingVertical: 8,
                    }}
                  >
                    <TextInput
                      ref={teamComposerRef}
                      value={draft}
                      onChangeText={(next) =>
                        setDraft((prev) => clampToWordCap(prev, next, CHAT_WORD_CAP))
                      }
                      placeholder="Message your team…"
                      placeholderTextColor={STUDENT_THEME.textOnSurfaceFaint}
                      multiline
                      style={{
                        flex: 1,
                        minHeight: 36,
                        maxHeight: 140,
                        paddingHorizontal: 4,
                        paddingVertical: 8,
                        fontFamily: FONTS.body,
                        fontSize: 14,
                        lineHeight: 21,
                        color: COLORS.FOREST.hex,
                        backgroundColor: "transparent",
                        outlineStyle: "none",
                      } as unknown as object}
                      onKeyPress={(e) => {
                        const ne = e.nativeEvent as unknown as { key?: string; shiftKey?: boolean };
                        if (Platform.OS === "web" && ne.key === "Enter" && !ne.shiftKey) {
                          e.preventDefault?.();
                          void onSend();
                        }
                      }}
                    />
                    <SendPlaneButton
                      onPress={() => void onSend()}
                      disabled={sending || draft.trim().length === 0}
                      sending={sending}
                    />
                  </View>
                  {/* Word counter — only renders when the user is past 80% of
                      the cap, so it doesn't clutter normal short messages. */}
                  {(() => {
                    const wordCount = countWords(draft);
                    const showCounter = wordCount >= Math.floor(CHAT_WORD_CAP * 0.8);
                    if (!showCounter) return null;
                    const atCap = wordCount >= CHAT_WORD_CAP;
                    return (
                      <Text
                        className="self-end text-[10.5px] uppercase"
                        style={{
                          fontFamily: FONTS.bodyBold,
                          letterSpacing: 1.2,
                          color: atCap ? STUDENT_THEME.destructive : COLORS.AMBER.hex,
                        }}
                      >
                        {`${wordCount} / ${CHAT_WORD_CAP} words${atCap ? " · cap reached" : ""}`}
                      </Text>
                    );
                  })()}
                </View>
              </View>

              {/* RIGHT — roster panel (rounded card). */}
              <View
                className="flex-col overflow-hidden rounded-[16px] border border-sand-dark bg-white md:w-[300px]"
                style={{ minHeight: 0 }}
              >
                {/* Roster header — title, count, and a small "+" icon to invite.
                    Hidden while a DM is open so the DmChatPanel's own header
                    (back arrow + "[Name] Chat") is the only thing on top. */}
                {openDmWith ? null : (
                  <View
                    className="flex-row items-baseline justify-between gap-2 border-b px-4 pt-4 pb-3 md:px-5"
                    style={{
                      flexShrink: 0,
                      backgroundColor: STUDENT_THEME.surfaceRaisedBg,
                      borderBottomColor: COLORS.SAND_LINE.hex,
                    }}
                  >
                    <Text
                      style={{
                        fontFamily: FONTS.displayMedium,
                        color: COLORS.FOREST.hex,
                        letterSpacing: -0.05,
                        fontSize: 18,
                        lineHeight: 22,
                      }}
                    >
                      Roster
                    </Text>
                    <Text
                      className="uppercase"
                      style={{
                        fontFamily: FONTS.bodyBold,
                        color: COLORS.AMBER_DEEP.hex,
                        letterSpacing: 1.6,
                        fontSize: 10,
                      }}
                    >
                      {`${detail.members.length} / ${detail.competition.max_team_size}`}
                    </Text>
                  </View>
                )}

                {/* The roster always renders here — DMs now open as a
                    centered overlay (see <DmOverlay/> at the page root)
                    instead of replacing this panel, so the user keeps the
                    member list visible underneath the blurred backdrop. */}
                <>
                {/* Members list — scrolls inside this column. */}
                <ScrollView
                  className="flex-1 bg-white"
                  style={{ minHeight: 0 }}
                  contentContainerStyle={{ paddingBottom: 4 }}
                >
                  {detail.members.map((m, idx) => {
                    const isSelf = m.student_id === myUserId;
                    const dmUnread =
                      myDmThreads.find((t) => t.otherUserId === m.student_id)?.unread ?? 0;
                    return (
                      <MemberRow
                        key={m.student_id}
                        member={m}
                        canKick={
                          detail.is_captain &&
                          m.student_id !== detail.team.owner_id &&
                          m.student_id !== myUserId
                        }
                        canPromote={detail.is_captain && !m.is_captain}
                        canMessage={!isSelf}
                        dmUnread={dmUnread}
                        onMessage={() =>
                          void onOpenDmWith(
                            m.student_id,
                            m.full_name ?? m.student_id.slice(0, 8),
                            m.avatar_url ?? null
                          )
                        }
                        onKick={() =>
                          onRequestKick(
                            m.student_id,
                            m.full_name ?? m.student_id.slice(0, 8)
                          )
                        }
                        onPromote={() =>
                          onRequestPromote(
                            m.student_id,
                            m.full_name ?? m.student_id.slice(0, 8)
                          )
                        }
                        canViewProfile={!isSelf}
                        onViewProfile={() => setPeerProfileFor(m.student_id)}
                        busy={busy}
                        first={idx === 0}
                      />
                    );
                  })}
                  {confirmingPromote ? (
                    <View
                      className="mx-3 mt-3 rounded-[6px] border px-3 py-3"
                      style={{
                        borderColor: STUDENT_THEME.accentBorderHover,
                        backgroundColor: "rgba(181,135,58,0.08)",
                      }}
                    >
                      <Text
                        className="text-[10px] font-bold uppercase"
                        style={{ fontFamily: FONTS.bodyBold, color: COLORS.AMBER.hex, letterSpacing: 1.4 }}
                      >
                        Promote to captain
                      </Text>
                      <Text
                        className="mt-1 text-[12.5px] leading-[18px]"
                        style={{ fontFamily: FONTS.bodyMedium, color: COLORS.FOREST.hex }}
                      >
                        {`Make ${confirmingPromote.name} a captain?`}
                      </Text>
                      <Text
                        className="mt-1 text-[11.5px] leading-[16px]"
                        style={{ fontFamily: FONTS.body, color: "rgba(28,43,26,0.78)" }}
                      >
                        Captains have full management rights — they can change
                        team settings, accept or decline join requests, edit
                        the team pitch, delete the team, and remove any member,
                        including you.
                      </Text>
                      <View className="mt-2 flex-row justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost-light"
                          onPress={() => setConfirmingPromote(null)}
                          disabled={busy}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          variant="primary"
                          onPress={() => void onPromoteConfirmed()}
                          disabled={busy}
                        >
                          Yes
                        </Button>
                      </View>
                    </View>
                  ) : null}

                  {/* Inline destructive confirm — anchored under the roster
                      when a captain has tapped the X icon on a member row.
                      Kicking is irreversible (the user has to be re-invited),
                      so the destructive treatment is intentional. */}
                  {confirmingKick ? (
                    <View
                      className="mx-3 mt-3 rounded-[6px] border px-3 py-3"
                      style={{
                        borderColor: "rgba(192,57,43,0.40)",
                        backgroundColor: "rgba(192,57,43,0.06)",
                      }}
                    >
                      <Text
                        className="text-[10px] font-bold uppercase"
                        style={{ fontFamily: FONTS.bodyBold, color: "#A33D2F", letterSpacing: 1.4 }}
                      >
                        Remove from team
                      </Text>
                      <Text
                        className="mt-1 text-[12.5px] leading-[18px]"
                        style={{ fontFamily: FONTS.bodyMedium, color: COLORS.FOREST.hex }}
                      >
                        {`Remove ${confirmingKick.name} from the team?`}
                      </Text>
                      <Text
                        className="mt-1 text-[11.5px] leading-[16px]"
                        style={{ fontFamily: FONTS.body, color: "rgba(28,43,26,0.78)" }}
                      >
                        They lose access to the team chat and any in-progress work this team is doing on this competition. To rejoin they would need a fresh invite link or to request to join again.
                      </Text>
                      <View className="mt-2 flex-row justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost-light"
                          onPress={() => setConfirmingKick(null)}
                          disabled={busy}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          variant="primary"
                          onPress={() => void onKickConfirmed()}
                          disabled={busy}
                        >
                          Remove
                        </Button>
                      </View>
                    </View>
                  ) : null}
                </ScrollView>

                {/* Add member — pinned to the bottom of the roster panel as a
                    full-width row. Sits inside the rounded card so the bottom
                    corners stay clipped cleanly. */}
                {detail.is_captain &&
                detail.members.length < detail.competition.max_team_size ? (
                  <View
                    style={{ flexShrink: 0 }}
                    className="border-t border-sand-dark"
                  >
                    <AddMemberRow onPress={() => setInviteOpen(true)} />
                  </View>
                ) : null}
                </>
              </View>
            </View>
          </View>
        )}

        {/* Pitch overlay — captain-only modal anchored to the page root. */}
        {detail?.is_captain ? (
          <PitchOverlay
            visible={pitchOverlayOpen}
            onClose={() => {
              setPitchOverlayOpen(false);
              void reload();
            }}
            teamId={detail.team.id}
            isCaptain={detail.is_captain}
            description={detail.team.description ?? null}
            lookingFor={detail.team.looking_for ?? null}
          />
        ) : null}

        {/* Invite-link panel — opens from the "Add member" affordance. */}
        {detail?.is_captain ? (
          <TeamInviteSection
            teamId={detail.team.id}
            isCaptain={detail.is_captain}
            open={inviteOpen}
            onOpenChange={setInviteOpen}
            hideTrigger
          />
        ) : null}

        {/* Direct-message overlay — opens when a roster message icon is
            tapped. Sits above everything else with a blurred backdrop so
            the team page stays visible underneath. */}
        <DmOverlay
          visible={openDmWith != null}
          other={openDmWith}
          messages={dmMessages}
          loading={dmLoading}
          draft={dmDraft}
          setDraft={setDmDraft}
          onSend={() => void onSendDm()}
          sending={dmSending}
          onClose={onCloseDm}
          onPressLink={(url, sender) => setLinkConfirm({ url, sender })}
          chatScrollRef={dmScrollRef}
          onAtBottomChange={setDmAtBottom}
          myUserId={myUserId}
          firstUnreadId={dmFirstUnreadId}
          onSetUnreadAnchorY={setDmUnreadAnchorY}
          onScrollY={setDmChatScrollY}
          showJumpToUnread={showJumpToDmUnread}
          onJumpToUnread={onJumpToDmUnread}
          onPressPeer={(sid) => setPeerProfileFor(sid)}
          atBottom={dmAtBottom}
          replyTo={dmReplyTo}
          onClearReply={() => setDmReplyTo(null)}
          onReply={onReplyDm}
          onDelete={onDeleteDm}
          composerRef={dmComposerRef}
        />

        {/* Program announcements overlay — opens when "See more" is tapped
            on any inline announcement event in the chat. Same overlay the
            dashboard uses, scoped to this team's competition. */}
        <ProgramAnnouncementsOverlay
          visible={announcementsOverlayOpen}
          onClose={() => setAnnouncementsOverlayOpen(false)}
          competitionId={detail?.competition?.id ?? ""}
          competitionName={detail?.competition?.name ?? null}
          seed={announcements}
          onChanged={(next) => setAnnouncements(next)}
          canDelete={!!detail?.is_captain}
        />

        {/* Captain-only megaphone composer — opens from the icon in the
            Team Chat header. Sends an announcement scoped to this team's
            competition, which then surfaces in every team's chat as a
            green sage event card + the program announcements overlay. */}
        {detail?.is_captain && detail.competition?.id ? (
          <MegaphoneCompose
            visible={megaphoneOpen}
            competitionId={detail.competition.id}
            competitionName={detail.competition.name ?? null}
            onClose={() => setMegaphoneOpen(false)}
            onPublished={() => {
              // Refresh announcements so the captain sees their own post
              // in chat without a hard reload. Realtime would do this too
              // but a direct refetch is faster on the local round-trip.
              const compId = detail.competition?.id;
              if (compId) {
                void listMyAnnouncements().then((all) => {
                  setAnnouncements(
                    all.filter(
                      (a) =>
                        a.audience_kind === "competition" &&
                        a.audience_competition_id === compId
                    )
                  );
                });
              }
            }}
          />
        ) : null}

        {/* Peer profile overlay — opens when any avatar / name is tapped on
            roster, team chat, DM panel, or pending-request rows. From here
            the captain can also fire off a DM directly. */}
        <PeerProfileOverlay
          studentId={peerProfileFor}
          onClose={() => setPeerProfileFor(null)}
          onPressMessage={(sid) => {
            // Find the member to seed name + avatar; fall back gracefully
            // if they're not on the team (e.g. pending requester whose
            // member row doesn't exist yet).
            const m = detail?.members.find((x) => x.student_id === sid);
            setPeerProfileFor(null);
            void onOpenDmWith(
              sid,
              m?.full_name ?? sid.slice(0, 8),
              m?.avatar_url ?? null
            );
          }}
        />
      </View>
    </>
  );
}

function ConfirmInline({
  prompt,
  confirmLabel,
  onCancel,
  onConfirm,
  busy,
  destructive = false,
}: {
  prompt: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
  busy: boolean;
  destructive?: boolean;
}) {
  return (
    <View
      className="mt-3 flex-row flex-wrap items-center gap-3 rounded-[6px] border px-3 py-2.5"
      style={{
        borderColor: destructive ? "rgba(192,57,43,0.35)" : "rgba(28,43,26,0.20)",
        backgroundColor: destructive ? "rgba(192,57,43,0.06)" : "rgba(28,43,26,0.03)",
      }}
    >
      <Text
        className="text-[12px]"
        style={{ fontFamily: FONTS.bodyMedium, color: "rgba(28,43,26,0.78)" }}
      >
        {prompt}
      </Text>
      <View className="ml-auto flex-row gap-2">
        <Button size="sm" variant="ghost-light" onPress={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" onPress={onConfirm} disabled={busy}>
          {confirmLabel}
        </Button>
      </View>
    </View>
  );
}

function MemberRow({
  member,
  canKick,
  canPromote,
  canMessage = false,
  canViewProfile = false,
  dmUnread = 0,
  onKick,
  onPromote,
  onMessage,
  onViewProfile,
  busy,
  first,
}: {
  member: TeamDetailMember;
  canKick: boolean;
  canPromote: boolean;
  canMessage?: boolean;
  /** True when the avatar+name region should open a peer profile overlay
   *  (false for the row representing the current user). */
  canViewProfile?: boolean;
  dmUnread?: number;
  onKick: () => void;
  onPromote: () => void;
  onMessage?: () => void;
  onViewProfile?: () => void;
  busy: boolean;
  first: boolean;
}) {
  const isCaptainRow = member.is_captain;
  // Hover state from the action icons — when any of this row's icons is
  // hovered, lift the entire row above sibling rows so the action tooltip
  // paints over the next member / Add member row instead of behind it.
  const [actionHover, setActionHover] = useState(false);
  return (
    <View
      className={`flex-row items-center justify-between px-4 py-3 ${first ? "" : "border-t border-sand-dark"}`}
      style={{
        position: "relative",
        zIndex: actionHover ? 30 : 1,
        backgroundColor: STUDENT_THEME.surfaceRaisedBg,
      }}
    >
      <Pressable
        onPress={canViewProfile && onViewProfile ? onViewProfile : undefined}
        accessibilityRole={canViewProfile ? "button" : undefined}
        accessibilityLabel={
          canViewProfile
            ? `View profile: ${member.full_name ?? "Member"}`
            : undefined
        }
        disabled={!canViewProfile}
        style={[
          { minWidth: 0, flex: 1, flexDirection: "row", alignItems: "center", gap: 12 },
          canViewProfile && Platform.OS === "web"
            ? ({ cursor: "pointer" } as const)
            : null,
        ]}
      >
        <Avatar name={member.full_name ?? "Member"} imageUrl={member.avatar_url} size="md" />
        <View className="min-w-0 flex-1">
          <Text
            className="text-[14px] text-forest"
            style={{ fontFamily: FONTS.bodyMedium }}
            numberOfLines={1}
          >
            {member.full_name ?? member.student_id.slice(0, 8)}
          </Text>
          <Text
            className="text-[11px] uppercase"
            style={{
              fontFamily: FONTS.bodyBold,
              color: isCaptainRow ? COLORS.AMBER.hex : STUDENT_THEME.textOnSurfaceFaint,
              letterSpacing: 1.4,
            }}
          >
            {isCaptainRow ? "Captain" : "Member"}
          </Text>
        </View>
      </Pressable>
      <View className="flex-row items-center gap-1.5">
        {canMessage ? (
          <RowIconAction
            label={dmUnread > 0 ? `Message · ${dmUnread} unread` : "Message"}
            description="Send a private message visible only to the two of you."
            tone="amber"
            onPress={() => onMessage?.()}
            disabled={busy}
            onHoverChange={setActionHover}
            icon={(c) => <ChatBubbleGlyph color={c} />}
            badgeCount={dmUnread > 0 ? dmUnread : null}
          />
        ) : null}
        {canPromote ? (
          <RowIconAction
            label="Make captain"
            description="Promote this member to captain. Captains can edit team settings, manage join requests, and remove members — including you."
            tone="amber"
            onPress={onPromote}
            disabled={busy}
            onHoverChange={setActionHover}
            icon={(c) => <CrownGlyph color={c} />}
          />
        ) : null}
        {canKick ? (
          <RowIconAction
            label="Remove from team"
            description="Kick this member off the team. They'll have to rejoin via a public team or a fresh invite link."
            tone="muted"
            onPress={onKick}
            disabled={busy}
            onHoverChange={setActionHover}
            icon={(c) => <CloseGlyph color={c} />}
          />
        ) : null}
      </View>
    </View>
  );
}

/**
 * Compact icon-only button used in roster rows. Square 28×28, neutral border,
 * hover tints toward the action color (amber for promote, dim red for kick).
 * Renders a forest tooltip card on hover (web only) with the label as title
 * and a longer description body — anchored above-right of the button so it
 * stays inside the right-rail panel.
 */
function RowIconAction({
  badgeCount = null,
  label,
  description,
  tone,
  onPress,
  disabled,
  onHoverChange,
  icon,
}: {
  badgeCount?: number | null;
  label: string;
  description: string;
  tone: "amber" | "muted";
  onPress: () => void;
  disabled: boolean;
  /** Optional callback so the parent row can lift its z-index while the
   *  tooltip is visible (prevents sibling rows from painting over it). */
  onHoverChange?: (hovered: boolean) => void;
  icon: (color: string) => React.ReactNode;
}) {
  const isWeb = Platform.OS === "web";
  const [hovered, setHovered] = useState(false);
  const isWebHover = isWeb && hovered && !disabled;
  const setHoverState = (next: boolean) => {
    setHovered(next);
    if (onHoverChange) onHoverChange(next);
  };
  return (
    // While hovered, lift the icon (and its tooltip) above sibling rows so the
    // tooltip card never paints behind the next row in the roster.
    <View style={{ position: "relative", zIndex: isWebHover ? 30 : 0 }}>
      <Pressable
        onPress={disabled ? undefined : onPress}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={label}
        {...(isWeb
          ? ({ onHoverIn: () => setHoverState(true), onHoverOut: () => setHoverState(false) } as Record<string, () => void>)
          : {})}
        style={[
          {
            width: 28,
            height: 28,
            borderRadius: 6,
            borderWidth: 1,
            alignItems: "center",
            justifyContent: "center",
            opacity: disabled ? 0.4 : 1,
            borderColor:
              isWebHover && tone === "amber"
                ? "rgba(181,135,58,0.55)"
                : isWebHover && tone === "muted"
                  ? "rgba(192,57,43,0.40)"
                  : "rgba(28,43,26,0.14)",
            backgroundColor:
              isWebHover && tone === "amber"
                ? STUDENT_THEME.accentSoft
                : isWebHover && tone === "muted"
                  ? "rgba(192,57,43,0.08)"
                  : "transparent",
          },
          isWeb && !disabled ? ({ cursor: "pointer" } as const) : null,
        ]}
      >
        {icon(
          isWebHover && tone === "amber"
            ? COLORS.AMBER.hex
            : isWebHover && tone === "muted"
              ? STUDENT_THEME.destructive
              : tone === "amber"
                ? COLORS.AMBER.hex
                : "rgba(28,43,26,0.55)"
        )}
        {/* Unread-count badge — small amber circle in the top-right of
            the icon button. Caps at 99+ so the bubble doesn't grow
            unbounded when there's a lot of backlog. */}
        {badgeCount != null && badgeCount > 0 ? (
          <View
            style={{
              position: "absolute",
              top: -5,
              right: -5,
              minWidth: 16,
              height: 16,
              paddingHorizontal: 4,
              borderRadius: 999,
              backgroundColor: COLORS.AMBER.hex,
              borderWidth: 1.5,
              borderColor: STUDENT_THEME.surfaceRaisedBg,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 9,
                color: COLORS.FOREST.hex,
                letterSpacing: 0.2,
                lineHeight: 11,
              }}
            >
              {badgeCount > 99 ? "99+" : String(badgeCount)}
            </Text>
          </View>
        ) : null}
      </Pressable>
      {isWebHover ? (
        <View
          pointerEvents="none"
          style={{
            // Drop the tooltip BELOW the button and anchor from the right so it
            // never clips above the viewport when the roster scrolls into the
            // sticky position near the top of the page. Width is tuned narrow
            // enough that even the LEFTMOST icon in a 3-icon action group
            // (Message → Promote → Kick) keeps the tooltip inside the right
            // rail without overflowing the panel's left edge.
            position: "absolute",
            top: 36,
            right: 0,
            width: 184,
            backgroundColor: COLORS.FOREST.hex,
            borderWidth: 1,
            borderColor: STUDENT_THEME.accentBorderSoft,
            paddingHorizontal: 12,
            paddingVertical: 9,
            borderRadius: 6,
            zIndex: 20,
            boxShadow: "0 12px 28px -14px rgba(0,0,0,0.45)",
          } as unknown as ViewStyle}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.4,
              letterSpacing: 1.6,
              textTransform: "uppercase",
              color: COLORS.AMBER.hex,
              marginBottom: 3,
            }}
          >
            {label}
          </Text>
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 11.5,
              lineHeight: 16,
              color: "rgba(247,244,238,0.78)",
            }}
          >
            {description}
          </Text>
          <View
            style={{
              position: "absolute",
              right: 11,
              top: -6,
              transform: [{ rotate: "45deg" }],
              width: 12,
              height: 12,
              backgroundColor: COLORS.FOREST.hex,
              borderLeftWidth: 1,
              borderTopWidth: 1,
              borderColor: STUDENT_THEME.accentBorderSoft,
            }}
          />
        </View>
      ) : null}
    </View>
  );
}

function PencilGlyph({ color }: { color: string }) {
  return (
    <Svg width={15} height={15} viewBox="0 0 16 16" fill="none">
      <Path
        d="M2.5 13.5L1 15l1.5-3.5L11 3l3 3-8.5 8.5L2.5 13.5z"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        fill="none"
      />
    </Svg>
  );
}

function CrownGlyph({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 16 16" fill="none">
      <Path
        d="M2.5 11.5 L3.5 5 L6 8 L8 4 L10 8 L12.5 5 L13.5 11.5 Z"
        stroke={color}
        strokeWidth={1.3}
        strokeLinejoin="round"
        strokeLinecap="round"
        fill="none"
      />
      <Path
        d="M3 11.5 H13"
        stroke={color}
        strokeWidth={1.3}
        strokeLinecap="round"
      />
    </Svg>
  );
}

function CloseGlyph({ color }: { color: string }) {
  return (
    <Svg width={12} height={12} viewBox="0 0 12 12" fill="none">
      <Path
        d="M2.5 2.5L9.5 9.5M9.5 2.5L2.5 9.5"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
      />
    </Svg>
  );
}

/**
 * Amber-rule divider with a centered "UNREAD" pill rendered just above the
 * first message the current user has not yet seen. Stays visible for the
 * duration of the visit; on next entry, the snapshot is fresh and the
 * divider naturally disappears.
 */
function UnreadDivider() {
  return (
    <View className="mt-3 mb-1 flex-row items-center gap-3">
      <View style={{ flex: 1, height: 1, backgroundColor: COLORS.AMBER.hex, opacity: 0.6 }} />
      <View
        style={{
          paddingHorizontal: 10,
          paddingVertical: 3,
          borderRadius: 999,
          backgroundColor: "rgba(181,135,58,0.14)",
          borderWidth: 1,
          borderColor: "rgba(181,135,58,0.55)",
        }}
      >
        <Text
          className="text-[10px] uppercase"
          style={{
            fontFamily: FONTS.bodyBold,
            color: COLORS.AMBER.hex,
            letterSpacing: 1.8,
          }}
        >
          Unread
        </Text>
      </View>
      <View style={{ flex: 1, height: 1, backgroundColor: COLORS.AMBER.hex, opacity: 0.6 }} />
    </View>
  );
}

function ChatMessageGroup({
  messages,
  isMine,
  myUserId,
  isCaptain,
  pinnedIds,
  highlightId,
  onPressLink,
  onPressSender,
  onReply,
  onTogglePin,
  onDelete,
}: {
  messages: TeamMessage[];
  isMine: boolean;
  myUserId: string | null;
  isCaptain: boolean;
  pinnedIds: Set<string>;
  highlightId: string | null;
  onPressLink: (url: string, sender: string) => void;
  /** Tap on the avatar or the sender name opens that user's profile. Not
   *  fired for the current user's own messages. */
  onPressSender?: (senderId: string) => void;
  onReply: (m: TeamMessage) => void;
  onTogglePin: (m: TeamMessage) => void;
  onDelete: (m: TeamMessage) => void;
}) {
  if (messages.length === 0) return null;
  const first = messages[0];
  const senderName = first.sender_name ?? "Member";
  const stickyAvatarStyle =
    Platform.OS === "web"
      ? ({
          width: 30,
          position: "sticky",
          top: 0,
          alignSelf: "flex-start",
          zIndex: 2,
          paddingTop: 4,
          paddingBottom: 4,
          backgroundColor: STUDENT_THEME.surfaceRaisedBg,
        } as unknown as object)
      : { width: 30, alignSelf: "flex-start" as const };
  // Only make the avatar / name clickable for messages that belong to a
  // teammate. Tapping your own avatar to "view your profile" is a tab in
  // the persistent header; doubling up here is noise.
  const peerClickable = !isMine && onPressSender != null;
  return (
    <View className="flex-row items-start gap-3">
      <Pressable
        onPress={peerClickable ? () => onPressSender!(first.sender_id) : undefined}
        accessibilityRole={peerClickable ? "button" : undefined}
        accessibilityLabel={peerClickable ? `View profile: ${senderName}` : undefined}
        disabled={!peerClickable}
        style={[
          stickyAvatarStyle,
          peerClickable && Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
        ]}
      >
        <Avatar name={senderName} imageUrl={first.sender_avatar_url} size="sm" />
      </Pressable>
      <View className="min-w-0 flex-1">
        <View className="flex-row items-baseline gap-2 pt-1">
          <Pressable
            onPress={peerClickable ? () => onPressSender!(first.sender_id) : undefined}
            accessibilityRole={peerClickable ? "button" : undefined}
            disabled={!peerClickable}
            style={
              peerClickable && Platform.OS === "web"
                ? ({ cursor: "pointer" } as const)
                : undefined
            }
          >
            <Text
              className="text-[13px] text-forest"
              style={{ fontFamily: FONTS.bodySemiBold }}
              numberOfLines={1}
            >
              {isMine ? `${senderName} (you)` : senderName}
            </Text>
          </Pressable>
          <Text
            className="text-[10px]"
            style={{ fontFamily: FONTS.body, color: STUDENT_THEME.textOnSurfaceFaint, letterSpacing: 0.4 }}
          >
            {formatTimestamp(first.created_at)}
          </Text>
        </View>
        <View style={{ paddingTop: 4, gap: 6 }}>
          {messages.map((m) => (
            <MessageRow
              key={m.id}
              m={m}
              isMine={isMine}
              isCaptain={isCaptain}
              myUserId={myUserId}
              pinned={pinnedIds.has(m.id)}
              highlighted={highlightId === m.id}
              senderNameForLink={senderName}
              onPressLink={onPressLink}
              onReply={onReply}
              onTogglePin={onTogglePin}
              onDelete={onDelete}
            />
          ))}
        </View>
      </View>
    </View>
  );
}

function AddMemberRow({ onPress }: { onPress: () => void }) {
  const isWeb = Platform.OS === "web";
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel="Add member"
      style={({ hovered }: { hovered?: boolean }) => [
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
          paddingHorizontal: 16,
          paddingVertical: 12,
          backgroundColor: hovered && isWeb ? "rgba(181,135,58,0.08)" : "transparent",
        },
        isWeb ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <View
        style={{
          width: 28,
          height: 28,
          borderRadius: 14,
          borderWidth: 1,
          borderStyle: "dashed",
          borderColor: "rgba(181,135,58,0.55)",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(181,135,58,0.08)",
        }}
      >
        <Svg width={12} height={12} viewBox="0 0 16 16" fill="none">
          <Path d="M8 3v10M3 8h10" stroke={COLORS.AMBER.hex} strokeWidth={1.6} strokeLinecap="round" />
        </Svg>
      </View>
      <View className="min-w-0 flex-1">
        <Text
          className="text-[12.5px]"
          style={{ fontFamily: FONTS.bodySemiBold, color: COLORS.AMBER.hex, letterSpacing: 0.2 }}
        >
          Add member
        </Text>
        <Text
          className="text-[10px]"
          style={{ fontFamily: FONTS.body, color: STUDENT_THEME.textOnSurfaceFaint }}
        >
          Invite by link or QR
        </Text>
      </View>
    </Pressable>
  );
}

function PitchOverlay({
  visible,
  onClose,
  teamId,
  isCaptain,
  description,
  lookingFor,
}: {
  visible: boolean;
  onClose: () => void;
  teamId: string;
  isCaptain: boolean;
  description: string | null;
  lookingFor: string | null;
}) {
  return (
    <HeaderOverlay
      visible={visible}
      onClose={onClose}
      title="Edit team pitch"
      subtitle="What other students see when browsing teams"
    >
      <View style={{ paddingHorizontal: 28, paddingVertical: 24 }}>
        <TeamPitchSection
          teamId={teamId}
          isCaptain={isCaptain}
          initialDescription={description}
          initialLookingFor={lookingFor}
        />
      </View>
    </HeaderOverlay>
  );
}


// ---------------------------------------------------------------------------
// MessageBody — splits a chat message string into plain-text segments and
// link segments, rendering each link as a tappable underline that calls
// onPressLink with the URL. Detection covers http(s)://… and www.… patterns
// and stops at whitespace. Trailing punctuation that often follows a link
// in prose (e.g. ".", ",", ")", "!", "?") is stripped from the URL.
// ---------------------------------------------------------------------------

const URL_RE = /\b((?:https?:\/\/|www\.)[^\s<>"]+)/gi;
const TRAILING_PUNCT = /[.,!?;:)\]}>'"]+$/;

function splitMessageBody(body: string): Array<{ kind: "text" | "url"; text: string }> {
  const out: Array<{ kind: "text" | "url"; text: string }> = [];
  let lastIndex = 0;
  for (const m of body.matchAll(URL_RE)) {
    const start = m.index ?? 0;
    let raw = m[0];
    let trailing = "";
    const trail = TRAILING_PUNCT.exec(raw);
    if (trail) {
      trailing = trail[0];
      raw = raw.slice(0, raw.length - trailing.length);
    }
    if (start > lastIndex) {
      out.push({ kind: "text", text: body.slice(lastIndex, start) });
    }
    out.push({ kind: "url", text: raw });
    if (trailing) out.push({ kind: "text", text: trailing });
    lastIndex = start + m[0].length;
  }
  if (lastIndex < body.length) {
    out.push({ kind: "text", text: body.slice(lastIndex) });
  }
  if (out.length === 0) out.push({ kind: "text", text: body });
  return out;
}

// MessageRow — wraps a single chat message with: optional quoted-reply
// preview, pinned dot, hover-revealed action toolbar (reply / pin /
// delete), grey ghost rendering for soft-deleted messages, and a brief
// amber background pulse when `highlighted` is true (used for jump-to-pin).
function MessageRow({
  m,
  isMine,
  isCaptain,
  myUserId,
  pinned,
  highlighted,
  senderNameForLink,
  onPressLink,
  onReply,
  onTogglePin,
  onDelete,
}: {
  m: TeamMessage;
  isMine: boolean;
  isCaptain: boolean;
  myUserId: string | null;
  pinned: boolean;
  highlighted: boolean;
  senderNameForLink: string;
  onPressLink: (url: string, sender: string) => void;
  onReply: (m: TeamMessage) => void;
  onTogglePin: (m: TeamMessage) => void;
  onDelete: (m: TeamMessage) => void;
}) {
  const isDeleted = m.deleted_at != null;
  // Sender or captain can delete; anyone in the team can pin/reply.
  const canDelete = !isDeleted && (m.sender_id === myUserId || isCaptain);

  if (isDeleted) {
    // "Message deleted by X" — use the deleter's resolved name when we
    // have it (the lib hydrates `deleted_by_name` from user_profiles). If
    // the deleter is the current user, show "you"; otherwise show their
    // first name. Fall back to plain "Message deleted" when the name
    // hasn't loaded yet.
    const deleterIsMe = m.deleted_by_user_id === myUserId;
    const fullDeleter = deleterIsMe
      ? "you"
      : m.deleted_by_name?.split(/\s+/)[0] ?? null;
    const ghostText = fullDeleter
      ? `Message deleted by ${fullDeleter}`
      : "Message deleted";
    return (
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          paddingVertical: 2,
        }}
      >
        <Svg width={11} height={11} viewBox="0 0 12 12" fill="none">
          <Path d="M3 3 L9 9 M9 3 L3 9" stroke="rgba(28,43,26,0.40)" strokeWidth={1.4} strokeLinecap="round" />
        </Svg>
        <Text
          style={{
            fontFamily: FONTS.body,
            fontStyle: "italic",
            color: STUDENT_THEME.textOnSurfaceFaint,
            fontSize: 12.5,
          }}
        >
          {ghostText}
        </Text>
      </View>
    );
  }

  return <MessageRowInner
    m={m}
    isMine={isMine}
    isCaptain={isCaptain}
    myUserId={myUserId}
    pinned={pinned}
    highlighted={highlighted}
    canDelete={canDelete}
    senderNameForLink={senderNameForLink}
    onPressLink={onPressLink}
    onReply={onReply}
    onTogglePin={onTogglePin}
    onDelete={onDelete}
  />;
}

// Inner component exists so the hover state stays scoped to a single row
// without cluttering MessageRow's prop checks. onHoverIn / onHoverOut are
// no-ops on native, so this is functionally just a static container off web.
function MessageRowInner({
  m,
  isMine,
  isCaptain,
  myUserId,
  pinned,
  highlighted,
  canDelete,
  senderNameForLink,
  onPressLink,
  onReply,
  onTogglePin,
  onDelete,
}: {
  m: TeamMessage;
  isMine: boolean;
  isCaptain: boolean;
  myUserId: string | null;
  pinned: boolean;
  highlighted: boolean;
  canDelete: boolean;
  senderNameForLink: string;
  onPressLink: (url: string, sender: string) => void;
  onReply: (m: TeamMessage) => void;
  onTogglePin: (m: TeamMessage) => void;
  onDelete: (m: TeamMessage) => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <View
      // RN-Web translates onMouseEnter/Leave from the underlying DOM, but
      // the cleanest cross-platform handle is Pressable's hover events.
      onPointerEnter={Platform.OS === "web" ? () => setHovered(true) : undefined}
      onPointerLeave={Platform.OS === "web" ? () => setHovered(false) : undefined}
      style={[
        {
          position: "relative",
          paddingTop: pinned || m.reply_to ? 6 : 0,
          paddingBottom: pinned ? 6 : 0,
          paddingHorizontal: pinned ? 10 : 6,
          marginHorizontal: -6,
          // Pinned messages get a plum-tinted box with a thin plum border
          // so they read as pinned right where they live in chat — no need
          // to scan the banner up top to figure out which message it was.
          borderRadius: 6,
          borderLeftWidth: pinned ? 3 : 0,
          borderLeftColor: pinned ? PLUM : "transparent",
          backgroundColor: highlighted
            ? STUDENT_THEME.accentHalo
            : pinned
            ? "rgba(132,90,140,0.10)"
            : hovered && Platform.OS === "web"
            ? "rgba(28,43,26,0.04)"
            : "transparent",
        },
      ]}
    >
            {/* No gutter indicator for pinned messages — the pinned-banner
                above the chat is the canonical "this is pinned" surface,
                and a second per-row mark just looked noisy next to the
                avatar column. The hover toolbar's filled-plum pin button
                still tells you when a message is pinned. */}

            {/* Quoted reply preview */}
            {m.reply_to ? (
              <View
                style={{
                  borderLeftWidth: 2,
                  borderLeftColor: "rgba(28,43,26,0.30)",
                  paddingLeft: 8,
                  marginBottom: 4,
                  opacity: 0.85,
                }}
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 11,
                    color: STUDENT_THEME.textOnSurfaceFaint,
                    letterSpacing: 0.2,
                  }}
                  numberOfLines={1}
                >
                  ↩  {m.reply_to.sender_name ?? "Member"}
                </Text>
                <Text
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 12,
                    color: STUDENT_THEME.textOnSurfaceDim,
                    fontStyle: m.reply_to.deleted ? "italic" : "normal",
                  }}
                  numberOfLines={1}
                >
                  {m.reply_to.deleted ? "Message deleted" : m.reply_to.body_preview}
                </Text>
              </View>
            ) : null}

            <MessageBody
              body={m.body}
              onPressLink={(url) => onPressLink(url, senderNameForLink)}
            />

            {/* Hover toolbar — only on web. Lives in the top-right of the row. */}
            {hovered && Platform.OS === "web" ? (
              <View
                style={{
                  position: "absolute",
                  top: -10,
                  right: 4,
                  flexDirection: "row",
                  gap: 2,
                  backgroundColor: STUDENT_THEME.surfaceRaisedBg,
                  borderRadius: 6,
                  borderWidth: 1,
                  borderColor: "rgba(28,43,26,0.14)",
                  padding: 2,
                  shadowColor: "#000",
                  shadowOpacity: 0.06,
                  shadowRadius: 4,
                  shadowOffset: { width: 0, height: 1 },
                }}
              >
                <ToolbarBtn label="Reply" onPress={() => onReply(m)}>
                  {/* Curved corner-up-left reply arrow — Lucide-style. The
                      hook + arc reads "this points back at something" the
                      way reply icons do in Gmail / iMessage. */}
                  <Svg width={14} height={14} viewBox="0 0 16 16" fill="none">
                    <Path
                      d="M6 4 L2 8 L6 12"
                      stroke={COLORS.FOREST.hex}
                      strokeWidth={1.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                    <Path
                      d="M2 8 H10 A3 3 0 0 1 13 11 V13"
                      stroke={COLORS.FOREST.hex}
                      strokeWidth={1.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </Svg>
                </ToolbarBtn>
                {/* Pin / unpin — captains only. Non-captains can still
                    SEE the pinned banner above the chat, but only the
                    captain can change what's pinned. */}
                {isCaptain ? (
                  <ToolbarBtn
                    label={pinned ? "Unpin" : "Pin"}
                    onPress={() => onTogglePin(m)}
                  >
                    {/* Tilted classic pushpin — cap in the upper-right, oval
                        flange in the middle, sharp needle pointing down-left.
                        Same silhouette every chat app uses for pinned
                        messages. Solid plum when pinned, outline when not. */}
                    <Svg width={14} height={14} viewBox="0 0 24 24" fill="none">
                      <Path
                        d="M13 1 L20 5 L18 9 L19 12 L14 13 L7 21 L10 11 L8 6 L11 5 Z"
                        stroke={pinned ? PLUM_DEEP : COLORS.FOREST.hex}
                        strokeWidth={1.4}
                        strokeLinejoin="round"
                        fill={pinned ? PLUM : "none"}
                      />
                    </Svg>
                  </ToolbarBtn>
                ) : null}
                {canDelete ? (
                  <ToolbarBtn label="Delete" onPress={() => onDelete(m)}>
                    {/* Trash bin with lid and two grip slots — reads as
                        a proper bin instead of a stick figure. */}
                    <Svg width={14} height={14} viewBox="0 0 16 16" fill="none">
                      <Path
                        d="M2.5 4 H13.5"
                        stroke={"#A33D2F"}
                        strokeWidth={1.5}
                        strokeLinecap="round"
                      />
                      <Path
                        d="M6 4 V2.5 A0.5 0.5 0 0 1 6.5 2 H9.5 A0.5 0.5 0 0 1 10 2.5 V4"
                        stroke={"#A33D2F"}
                        strokeWidth={1.5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                      <Path
                        d="M4.5 4 L5.25 13.25 A1 1 0 0 0 6.25 14.25 H9.75 A1 1 0 0 0 10.75 13.25 L11.5 4"
                        stroke={"#A33D2F"}
                        strokeWidth={1.5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                      <Path
                        d="M7 7 V12 M9 7 V12"
                        stroke={"#A33D2F"}
                        strokeWidth={1.4}
                        strokeLinecap="round"
                      />
                    </Svg>
                  </ToolbarBtn>
                ) : null}
              </View>
            ) : null}
    </View>
  );
}

// Circular amber send button — replaces the textual "Send" button in
// both the team chat and DM composers. Hover darkens, disabled fades.
// The plane is a Lucide-style origami triangle with a fold line for
// depth.
function SendPlaneButton({
  onPress,
  disabled,
  sending,
}: {
  onPress: () => void;
  disabled?: boolean;
  sending?: boolean;
}) {
  const isWeb = Platform.OS === "web";
  return (
    <Pressable
      onPress={() => {
        if (!disabled) onPress();
      }}
      disabled={!!disabled}
      accessibilityRole="button"
      accessibilityLabel={sending ? "Sending message" : "Send message"}
      hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
      style={({ hovered }: { hovered?: boolean }) => [
        {
          width: 38,
          height: 38,
          borderRadius: 999,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: disabled
            ? "rgba(181,135,58,0.32)"
            : hovered && isWeb
            ? COLORS.AMBER_DEEP.hex
            : COLORS.AMBER.hex,
        },
        isWeb
          ? ({
              cursor: disabled ? "not-allowed" : "pointer",
              transition: "background-color 0.18s ease",
            } as unknown as object)
          : null,
      ]}
    >
      {/* The plane shape is asymmetric (mass leans top-right toward the
          tip), so its geometric bounding box is centered in the viewBox
          but the optical center sits slightly up-right. We compensate
          by nudging the whole SVG down-left a hair so the icon reads
          as visually centered inside the amber circle. */}
      <Svg
        width={16}
        height={16}
        viewBox="0 0 24 24"
        fill="none"
        style={
          isWeb
            ? ({ transform: "translate(-1px, 1px)" } as unknown as object)
            : undefined
        }
      >
        <Path
          d="M22 2 L11 13"
          stroke={COLORS.FOREST.hex}
          strokeWidth={1.7}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <Path
          d="M22 2 L15 22 L11 13 L2 9 Z"
          stroke={COLORS.FOREST.hex}
          strokeWidth={1.7}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill={COLORS.FOREST.hex}
        />
      </Svg>
    </Pressable>
  );
}

function ToolbarBtn({
  children,
  onPress,
  label,
}: {
  children: React.ReactNode;
  onPress: () => void;
  label: string;
}) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={({ hovered }: { hovered?: boolean }) => [
        {
          width: 24,
          height: 24,
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 4,
          backgroundColor: hovered && Platform.OS === "web" ? "rgba(28,43,26,0.06)" : "transparent",
        },
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      {children}
    </Pressable>
  );
}

// ---------------------------------------------------------------------------
// PinnedBanner — plum-tinted strip above the chat scroll. Collapsed shows
// the most-recent pin only. Expanded shows up to 5 stacked rows. Captain
// can unpin from each row.
// ---------------------------------------------------------------------------

function PinnedBanner({
  pinned,
  expanded,
  onToggleExpand,
  onJump,
  canUnpin,
  onUnpin,
}: {
  pinned: PinnedMessage[];
  expanded: boolean;
  onToggleExpand: () => void;
  onJump: (messageId: string) => void;
  canUnpin: boolean;
  onUnpin: (messageId: string) => Promise<void> | void;
}) {
  if (pinned.length === 0) return null;
  const head = pinned[0];
  const more = pinned.length - 1;
  const visibleList = expanded ? pinned : [head];
  return (
    <View
      style={{
        backgroundColor: PLUM_BG,
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderColor: PLUM_BORDER,
        paddingHorizontal: 16,
        paddingVertical: 8,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 4, gap: 6 }}>
        <Svg width={13} height={13} viewBox="0 0 24 24" fill="none">
          <Path
            d="M13 1 L20 5 L18 9 L19 12 L14 13 L7 21 L10 11 L8 6 L11 5 Z"
            fill={PLUM}
            stroke={PLUM_DEEP}
            strokeWidth={0.8}
            strokeLinejoin="round"
          />
        </Svg>
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 10,
            letterSpacing: 1.4,
            color: PLUM_DEEP,
          }}
        >
          PINNED{pinned.length > 1 ? ` · ${pinned.length}` : ""}
        </Text>
        {pinned.length > 1 ? (
          <Pressable
            onPress={onToggleExpand}
            style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : undefined}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 10,
                letterSpacing: 1.2,
                color: PLUM_DEEP,
                marginLeft: 6,
              }}
            >
              {expanded ? "HIDE" : `VIEW ALL (${more} more)`}
            </Text>
          </Pressable>
        ) : null}
      </View>
      <View style={{ gap: 4 }}>
        {visibleList.map((p) => (
          <Pressable
            key={p.message_id}
            onPress={() => onJump(p.message_id)}
            style={({ hovered }: { hovered?: boolean }) => [
              {
                flexDirection: "row",
                alignItems: "center",
                gap: 8,
                paddingVertical: 4,
                paddingHorizontal: 6,
                marginHorizontal: -6,
                borderRadius: 4,
                backgroundColor: hovered && Platform.OS === "web" ? "rgba(132,90,140,0.08)" : "transparent",
              },
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 12,
                color: PLUM_DEEP,
              }}
              numberOfLines={1}
            >
              {p.message?.sender_name ?? "Member"}
            </Text>
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 12,
                color: COLORS.FOREST.hex,
                flex: 1,
              }}
              numberOfLines={1}
            >
              {p.message?.deleted_at
                ? "Message deleted"
                : p.message?.body ?? "(unavailable)"}
            </Text>
            {canUnpin ? (
              <Pressable
                onPress={(e) => {
                  // Don't trigger the row jump; just unpin.
                  e.stopPropagation?.();
                  void onUnpin(p.message_id);
                }}
                style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : undefined}
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 10,
                    letterSpacing: 1.2,
                    color: PLUM_DEEP,
                  }}
                >
                  UNPIN
                </Text>
              </Pressable>
            ) : null}
          </Pressable>
        ))}
      </View>
    </View>
  );
}

function MessageBody({
  body,
  onPressLink,
}: {
  body: string;
  onPressLink: (url: string) => void;
}) {
  const parts = splitMessageBody(body);
  return (
    <Text
      className="text-[14px] leading-5 text-forest"
      style={{ fontFamily: FONTS.body }}
    >
      {parts.map((p, i) =>
        p.kind === "text" ? (
          <Text key={i}>{p.text}</Text>
        ) : (
          <Text
            key={i}
            onPress={() => onPressLink(p.text)}
            style={[
              {
                color: "#7A5A1F",
                textDecorationLine: "underline",
                fontFamily: FONTS.bodyMedium,
              },
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
            accessibilityRole="link"
          >
            {p.text}
          </Text>
        )
      )}
    </Text>
  );
}

// ---------------------------------------------------------------------------
// LinkWarningModal — confirm before opening any link from a chat message.
// Mounted once at the page level, driven by linkConfirm state.
// ---------------------------------------------------------------------------

function LinkWarningModal({
  state,
  onClose,
}: {
  state: { url: string; sender: string } | null;
  onClose: () => void;
}) {
  const open = state != null;
  function onOpen() {
    if (!state) return;
    let url = state.url;
    // www-prefixed URLs need a scheme so the OS handler can open them.
    if (!/^https?:\/\//i.test(url)) url = "https://" + url;
    onClose();
    void Linking.openURL(url).catch(() => {
      // openURL can throw on web if the popup is blocked; fall back to a
      // plain window.open which is allowed because it's still inside the
      // user-activation window of the original tap.
      if (Platform.OS === "web" && typeof window !== "undefined") {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    });
  }
  return (
    <Modal visible={open} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        onPress={onClose}
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.55)",
          alignItems: "center",
          justifyContent: "center",
          paddingHorizontal: 18,
        }}
      >
        <Pressable
          onPress={() => {}}
          style={{
            width: "100%",
            maxWidth: 460,
            backgroundColor: STUDENT_THEME.surfaceRaisedBg,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: COLORS.SAND_DARK.hex,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <View style={{ paddingHorizontal: 22, paddingTop: 22, paddingBottom: 10, gap: 6 }}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
              <View
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 999,
                  backgroundColor: "rgba(181,135,58,0.14)",
                  borderWidth: 1,
                  borderColor: "rgba(181,135,58,0.55)",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 11, color: COLORS.AMBER.hex }}>!</Text>
              </View>
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 9,
                  letterSpacing: 1.6,
                  textTransform: "uppercase",
                  color: COLORS.AMBER.hex,
                }}
              >
                External link
              </Text>
            </View>
            <Text
              style={{
                fontFamily: "Cormorant_500Medium",
                fontSize: 22,
                color: COLORS.FOREST.hex,
                lineHeight: 26,
              }}
            >
              Do you trust this link?
            </Text>
          </View>

          {/* Body section: explains who sent the link and shows the URL in a
              chip so the user can sanity-check before navigating. */}
          <View style={{ paddingHorizontal: 22, paddingBottom: 14, gap: 10 }}>
            <Text
              className="text-[12.5px] leading-[18px]"
              style={{ fontFamily: FONTS.body, color: "rgba(28,43,26,0.78)" }}
            >
              {state
                ? "Sent by " + state.sender + ". We don't pre-screen links — open only if you recognize the destination."
                : ""}
            </Text>
            <View
              style={{
                paddingHorizontal: 12,
                paddingVertical: 10,
                borderRadius: 6,
                borderWidth: 1,
                borderColor: COLORS.SAND_DARK.hex,
                backgroundColor: COLORS.OFFW.hex,
              }}
            >
              <Text
                className="text-[10px] uppercase"
                style={{
                  fontFamily: FONTS.bodyBold,
                  color: STUDENT_THEME.textOnSurfaceFaint,
                  letterSpacing: 1.4,
                  marginBottom: 4,
                }}
              >
                URL
              </Text>
              <Text
                style={{
                  fontFamily: FONTS.bodyMedium,
                  fontSize: 12.5,
                  color: COLORS.FOREST.hex,
                  lineHeight: 17,
                }}
                selectable
              >
                {state?.url ?? ""}
              </Text>
            </View>
          </View>

          {/* Footer: Cancel + Open buttons. */}
          <View
            style={{
              flexDirection: "row",
              justifyContent: "flex-end",
              gap: 10,
              paddingHorizontal: 22,
              paddingVertical: 14,
              borderTopWidth: 1,
              borderTopColor: COLORS.SAND_DARK.hex,
              backgroundColor: "rgba(28,43,26,0.02)",
            }}
          >
            <Pressable
              onPress={onClose}
              style={({ hovered }: { hovered?: boolean }) => [
                {
                  paddingHorizontal: 16,
                  paddingVertical: 9,
                  borderRadius: 6,
                  borderWidth: 1,
                  borderColor: hovered ? STUDENT_THEME.textOnSurfaceFaint : "rgba(28,43,26,0.25)",
                  backgroundColor: hovered ? "rgba(28,43,26,0.04)" : "transparent",
                },
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text
                className="text-[11px] uppercase"
                style={{
                  fontFamily: FONTS.bodyBold,
                  color: COLORS.FOREST.hex,
                  letterSpacing: 1.2,
                }}
              >
                Cancel
              </Text>
            </Pressable>
            <Pressable
              onPress={onOpen}
              style={({ hovered }: { hovered?: boolean }) => [
                {
                  paddingHorizontal: 16,
                  paddingVertical: 9,
                  borderRadius: 6,
                  backgroundColor: hovered ? COLORS.AMBER_DEEP.hex : COLORS.AMBER.hex,
                },
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text
                className="text-[11px] uppercase"
                style={{
                  fontFamily: FONTS.bodyBold,
                  color: STUDENT_THEME.surfaceRaisedBg,
                  letterSpacing: 1.2,
                }}
              >
                Open
              </Text>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// TeamEventRow: grey system-message row that renders inline among chat
// messages. Backed by team_events; copy template depends on the event kind
// and on whether actor_id == target_id (self-action) vs different.
// Centered, low-contrast, no avatar - the goal is "barely there".
// ---------------------------------------------------------------------------

function teamEventCopy(event: TeamEvent, myUserId: string | null): string {
  const target = event.target_name ?? "Member";
  const actor = event.actor_name ?? "A captain";
  const targetIsMe = event.target_id === myUserId;
  const actorIsMe = event.actor_id != null && event.actor_id === myUserId;
  const subject = targetIsMe ? "You" : target;
  switch (event.kind) {
    case "joined": {
      if (event.actor_id && event.actor_id !== event.target_id) {
        const by = actorIsMe ? "you" : actor;
        return subject + " joined, accepted by " + by;
      }
      return subject + " joined the team";
    }
    case "left":
      return subject + " left the team";
    case "removed": {
      const by = actorIsMe ? "you" : actor;
      return subject + (targetIsMe ? " were" : " was") + " removed by " + by;
    }
    case "promoted_captain": {
      if (event.actor_id && event.actor_id !== event.target_id) {
        const by = actorIsMe ? "you" : actor;
        return subject + (targetIsMe ? " were" : " was") + " promoted to captain by " + by;
      }
      return subject + (targetIsMe ? " are" : " is") + " now captain";
    }
    case "demoted_captain": {
      if (event.actor_id && event.actor_id !== event.target_id) {
        const by = actorIsMe ? "you" : actor;
        return subject + (targetIsMe ? " were" : " was") + " demoted by " + by;
      }
      return subject + " stepped down as captain";
    }
    default:
      return subject + " updated the team";
  }
}

function TeamEventRow({
  event,
  myUserId,
}: {
  event: TeamEvent;
  myUserId: string | null;
}) {
  const copy = teamEventCopy(event, myUserId);
  return (
    // Centered text only — the horizontal divider lines were too visually
    // heavy when there were several events back-to-back. The muted text
    // alone is plenty to read as a "system row" and lets the membership
    // history breathe.
    <View
      style={{
        alignItems: "center",
        justifyContent: "center",
        paddingVertical: 4,
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyMedium,
          fontSize: 11,
          color: STUDENT_THEME.textOnSurfaceFaint,
          letterSpacing: 0.2,
          textAlign: "center",
        }}
      >
        {copy}
        <Text style={{ color: "rgba(28,43,26,0.30)" }}>
          {"  ·  " + formatTimestamp(event.created_at)}
        </Text>
      </Text>
    </View>
  );
}

// ---------------------------------------------------------------------------
// ChatBubbleGlyph: small chat-bubble icon used by the roster's "Message"
// row action.
// ---------------------------------------------------------------------------

function ChatBubbleGlyph({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 16 16" fill="none">
      <Path
        d="M2.5 4.5C2.5 3.4 3.4 2.5 4.5 2.5h7C12.6 2.5 13.5 3.4 13.5 4.5v5C13.5 10.6 12.6 11.5 11.5 11.5H6L3.5 13.5V11.5C3 11.5 2.5 11 2.5 10.5v-6Z"
        stroke={color}
        strokeWidth={1.4}
        strokeLinejoin="round"
        fill="none"
      />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// DmChatPanel: when a roster row's "Message" icon is clicked, the roster
// panel's body is replaced with this 1:1 chat. Same look-and-feel as the
// team chat (sender groups, avatars, link-warning, unread divider, jump-to-
// unread arrow), with a header that has a Back arrow + the other person's
// name.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// DmOverlay — wraps DmChatPanel in a centered modal with a blurred backdrop
// and a forest-green header chrome (so DMs read distinctly from the linen
// team-chat). Click backdrop or hit ESC to close. The DmChatPanel inside
// keeps every existing feature (sticky avatars, unread divider, jump-to-
// unread arrow, sticky scroll-to-bottom, peer-profile open on avatar tap).
// ---------------------------------------------------------------------------

type DmOverlayPanelProps = React.ComponentProps<typeof DmChatPanel>;

function DmOverlay(
  props: { visible: boolean } & Omit<DmOverlayPanelProps, "other"> & {
    other: DmOverlayPanelProps["other"] | null;
  }
) {
  const { visible, other, onClose } = props;
  // ESC closes — paired with the backdrop press.
  useEffect(() => {
    if (!visible || Platform.OS !== "web" || typeof window === "undefined") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, onClose]);

  if (!visible || !other) return null;

  return (
    <Modal visible={visible} transparent animationType="none" onRequestClose={onClose}>
      <Pressable
        onPress={onClose}
        style={[
          {
            flex: 1,
            backgroundColor: "rgba(14,14,14,0.55)",
            alignItems: "center",
            justifyContent: "center",
            paddingHorizontal: 24,
            paddingVertical: 32,
          },
          Platform.OS === "web"
            ? ({
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                // Default cursor so the dim+blur backdrop doesn't read
                // as clickable everywhere — only inside controls do.
                cursor: "default",
              } as object)
            : null,
        ]}
      >
        <Pressable
          // Inner Pressable swallows clicks so they don't bubble to the
          // backdrop and close the modal.
          onPress={(e) => e.stopPropagation?.()}
          style={[
            {
              // Match the team-chat surface dimensions: wider than a
              // typical modal so the DM doesn't feel cramped, capped so
              // it doesn't span ultra-wide displays.
              width: "100%",
              maxWidth: 1100,
              height: "100%",
              maxHeight: 900,
              backgroundColor: STUDENT_THEME.surfaceRaisedBg,
              borderRadius: 10,
              overflow: "hidden",
              borderWidth: 1,
              borderColor: "rgba(28,43,26,0.18)",
            },
            Platform.OS === "web"
              ? ({
                  boxShadow: "0 24px 48px -12px rgba(0,0,0,0.45)",
                  // Same reason as the backdrop: the panel surface isn't
                  // clickable, only its buttons. Don't lie with the cursor.
                  cursor: "default",
                } as unknown as object)
              : null,
          ]}
        >
          {/* Forest header bar with "DIRECT MESSAGE" label, the other
              person's avatar + name, and a close X. Higher contrast than
              the inline takeover so the DM reads as a distinct context. */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
              paddingHorizontal: 18,
              paddingVertical: 12,
              backgroundColor: COLORS.FOREST.hex,
              borderBottomWidth: 1,
              borderBottomColor: "rgba(255,255,255,0.10)",
            }}
          >
            <View style={{ flex: 1, minWidth: 0, gap: 2 }}>
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 9.5,
                  letterSpacing: 1.8,
                  color: COLORS.AMBER.hex,
                }}
              >
                DIRECT MESSAGE
              </Text>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
                <Avatar name={other.name} imageUrl={other.avatarUrl} size="sm" />
                <Text
                  style={{
                    fontFamily: FONTS.displayMedium,
                    fontSize: 19,
                    color: STUDENT_THEME.surfaceRaisedBg,
                  }}
                  numberOfLines={1}
                >
                  {other.name}
                </Text>
              </View>
            </View>
            <Pressable
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Close direct message"
              style={({ hovered }: { hovered?: boolean }) => [
                {
                  width: 30,
                  height: 30,
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 4,
                  borderWidth: 1,
                  borderColor:
                    hovered && Platform.OS === "web"
                      ? "rgba(255,255,255,0.55)"
                      : "rgba(255,255,255,0.30)",
                  backgroundColor:
                    hovered && Platform.OS === "web"
                      ? "rgba(255,255,255,0.10)"
                      : "transparent",
                },
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Svg width={12} height={12} viewBox="0 0 12 12" fill="none">
                <Path
                  d="M2 2 L10 10 M10 2 L2 10"
                  stroke={STUDENT_THEME.surfaceRaisedBg}
                  strokeWidth={1.6}
                  strokeLinecap="round"
                />
              </Svg>
            </Pressable>
          </View>

          {/* The DmChatPanel renders its own internal header (with a back
              arrow) and the chat body + composer. We hide the internal
              header via a top-padding nudge — but keep the chat body /
              composer untouched so all existing behavior carries. */}
          <View style={{ flex: 1, minHeight: 0, position: "relative" }}>
            <DmChatPanel
              other={other}
              messages={props.messages}
              loading={props.loading}
              draft={props.draft}
              setDraft={props.setDraft}
              onSend={props.onSend}
              sending={props.sending}
              onClose={onClose}
              onPressLink={props.onPressLink}
              chatScrollRef={props.chatScrollRef}
              onAtBottomChange={props.onAtBottomChange}
              myUserId={props.myUserId}
              firstUnreadId={props.firstUnreadId}
              onSetUnreadAnchorY={props.onSetUnreadAnchorY}
              onScrollY={props.onScrollY}
              showJumpToUnread={props.showJumpToUnread}
              onJumpToUnread={props.onJumpToUnread}
              onPressPeer={props.onPressPeer}
              atBottom={props.atBottom}
              replyTo={props.replyTo}
              onClearReply={props.onClearReply}
              onReply={props.onReply}
              onDelete={props.onDelete}
              composerRef={props.composerRef}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// DmMessageRow — single DM body with optional quoted reply preview, ghost
// rendering for soft-deletes, and a hover toolbar (reply for any DM,
// delete only for your own messages).
function DmMessageRow({
  m,
  isMine,
  myUserId,
  senderNameForLink,
  onPressLink,
  onReply,
  onDelete,
}: {
  m: DmMessage;
  isMine: boolean;
  myUserId: string | null;
  senderNameForLink: string;
  onPressLink: (url: string, sender: string) => void;
  onReply?: (m: DmMessage) => void;
  onDelete?: (m: DmMessage) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const isDeleted = m.deleted_at != null;
  const canDelete = !isDeleted && m.sender_id === myUserId;

  if (isDeleted) {
    const deleterIsMe = m.deleted_by_user_id === myUserId;
    const fullDeleter = deleterIsMe
      ? "you"
      : m.deleted_by_name?.split(/\s+/)[0] ?? null;
    const ghostText = fullDeleter ? `Message deleted by ${fullDeleter}` : "Message deleted";
    return (
      <View style={{ flexDirection: "row", alignItems: "center", gap: 6, paddingVertical: 2 }}>
        <Svg width={11} height={11} viewBox="0 0 12 12" fill="none">
          <Path d="M3 3 L9 9 M9 3 L3 9" stroke="rgba(28,43,26,0.40)" strokeWidth={1.4} strokeLinecap="round" />
        </Svg>
        <Text
          style={{
            fontFamily: FONTS.body,
            fontStyle: "italic",
            color: STUDENT_THEME.textOnSurfaceFaint,
            fontSize: 12.5,
          }}
        >
          {ghostText}
        </Text>
      </View>
    );
  }

  // Suppress unused warning when canDelete computes to false in builds
  // that strip dead branches.
  void isMine;

  return (
    <View
      onPointerEnter={Platform.OS === "web" ? () => setHovered(true) : undefined}
      onPointerLeave={Platform.OS === "web" ? () => setHovered(false) : undefined}
      style={{
        position: "relative",
        paddingTop: m.reply_to ? 4 : 0,
        paddingHorizontal: 6,
        marginHorizontal: -6,
        borderRadius: 6,
        backgroundColor:
          hovered && Platform.OS === "web" ? "rgba(28,43,26,0.04)" : "transparent",
      }}
    >
      {m.reply_to ? (
        <View
          style={{
            borderLeftWidth: 2,
            borderLeftColor: "rgba(28,43,26,0.30)",
            paddingLeft: 8,
            marginBottom: 4,
            opacity: 0.85,
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 11,
              color: STUDENT_THEME.textOnSurfaceFaint,
              letterSpacing: 0.2,
            }}
            numberOfLines={1}
          >
            ↩  {m.reply_to.sender_name ?? "Member"}
          </Text>
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 12,
              color: STUDENT_THEME.textOnSurfaceDim,
              fontStyle: m.reply_to.deleted ? "italic" : "normal",
            }}
            numberOfLines={1}
          >
            {m.reply_to.deleted ? "Message deleted" : m.reply_to.body_preview}
          </Text>
        </View>
      ) : null}

      <MessageBody body={m.body} onPressLink={(url) => onPressLink(url, senderNameForLink)} />

      {hovered && Platform.OS === "web" ? (
        <View
          style={{
            position: "absolute",
            top: -10,
            right: 4,
            flexDirection: "row",
            gap: 2,
            backgroundColor: STUDENT_THEME.surfaceRaisedBg,
            borderRadius: 6,
            borderWidth: 1,
            borderColor: "rgba(28,43,26,0.14)",
            padding: 2,
            shadowColor: "#000",
            shadowOpacity: 0.06,
            shadowRadius: 4,
            shadowOffset: { width: 0, height: 1 },
          }}
        >
          {onReply ? (
            <ToolbarBtn label="Reply" onPress={() => onReply(m)}>
              <Svg width={13} height={13} viewBox="0 0 16 16" fill="none">
                <Path
                  d="M6 4 L2 8 L6 12"
                  stroke={COLORS.FOREST.hex}
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
                <Path
                  d="M2 8 H10 A3 3 0 0 1 13 11 V13"
                  stroke={COLORS.FOREST.hex}
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </Svg>
            </ToolbarBtn>
          ) : null}
          {canDelete && onDelete ? (
            <ToolbarBtn label="Delete" onPress={() => onDelete(m)}>
              <Svg width={13} height={13} viewBox="0 0 16 16" fill="none">
                <Path
                  d="M2.5 4 H13.5"
                  stroke={"#A33D2F"}
                  strokeWidth={1.5}
                  strokeLinecap="round"
                />
                <Path
                  d="M6 4 V2.5 A0.5 0.5 0 0 1 6.5 2 H9.5 A0.5 0.5 0 0 1 10 2.5 V4"
                  stroke={"#A33D2F"}
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
                <Path
                  d="M4.5 4 L5.25 13.25 A1 1 0 0 0 6.25 14.25 H9.75 A1 1 0 0 0 10.75 13.25 L11.5 4"
                  stroke={"#A33D2F"}
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
                <Path
                  d="M7 7 V12 M9 7 V12"
                  stroke={"#A33D2F"}
                  strokeWidth={1.4}
                  strokeLinecap="round"
                />
              </Svg>
            </ToolbarBtn>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

function DmChatPanel({
  other,
  messages,
  loading,
  draft,
  setDraft,
  onSend,
  sending,
  onClose,
  onPressLink,
  chatScrollRef,
  onAtBottomChange,
  myUserId,
  firstUnreadId,
  onSetUnreadAnchorY,
  onScrollY,
  showJumpToUnread,
  onJumpToUnread,
  onPressPeer,
  atBottom,
  replyTo,
  onClearReply,
  onReply,
  onDelete,
  composerRef,
}: {
  other: { studentId: string; name: string; avatarUrl: string | null; threadId: string | null };
  messages: DmMessage[];
  loading: boolean;
  draft: string;
  setDraft: (next: string) => void;
  onSend: () => void;
  sending: boolean;
  onClose: () => void;
  onPressLink: (url: string, sender: string) => void;
  chatScrollRef: React.MutableRefObject<ScrollView | null>;
  onAtBottomChange: (atBottom: boolean) => void;
  myUserId: string | null;
  firstUnreadId: string | null;
  onSetUnreadAnchorY: (y: number | null) => void;
  onScrollY: (y: number) => void;
  showJumpToUnread: boolean;
  onJumpToUnread: () => void;
  /** Optional — tap on a sender's avatar/name opens their profile. */
  onPressPeer?: (studentId: string) => void;
  /** Optional — caller-side mirror of the chat's at-bottom state. Not
   *  used inside the panel today but accepted for API parity with
   *  ChatMessageGroup so callers can pipe a single value down. */
  atBottom?: boolean;
  /** Currently-staged reply target (null when not replying). */
  replyTo?: DmMessage | null;
  /** Cancel the staged reply (✕ on the composer pill). */
  onClearReply?: () => void;
  /** Hover-toolbar handlers — reply works for any DM message; delete is
   *  gated to the sender's own messages by the row component. */
  onReply?: (m: DmMessage) => void;
  onDelete?: (m: DmMessage) => void;
  /** Optional ref to the composer's TextInput so the parent can focus it
   *  programmatically (e.g. after staging a reply). */
  composerRef?: React.MutableRefObject<TextInput | null>;
}) {
  // Accept-but-ignore shimming for atBottom (kept in destructure so the
  // type of {atBottom} is consistent with the props object).
  void atBottom;
  void onPressPeer;
  const initialScrollDoneRef = useRef(false);
  const isWeb = Platform.OS === "web";

  type Group = { id: string; sender_id: string; messages: DmMessage[] };
  const groups: Group[] = [];
  for (const m of messages) {
    const last = groups[groups.length - 1];
    const lastMsg = last ? last.messages[last.messages.length - 1] : null;
    const closeInTime =
      lastMsg != null &&
      new Date(m.created_at).getTime() - new Date(lastMsg.created_at).getTime() <
        5 * 60 * 1000;
    if (last && last.sender_id === m.sender_id && closeInTime) {
      last.messages.push(m);
    } else {
      groups.push({ id: m.id, sender_id: m.sender_id, messages: [m] });
    }
  }

  return (
    <>
      {/* Header is now provided by the DmOverlay shell — the inline back
          arrow + avatar that used to live here was redundant inside the
          modal. The component still owns the chat body + composer. */}
      {/* Messages: scrollable, mirrors team-chat behavior. */}
      <View className="flex-1" style={{ minHeight: 0, position: "relative" }}>
        <ScrollView
          ref={chatScrollRef}
          className="flex-1 bg-white"
          style={{ minHeight: 0 }}
          contentContainerStyle={{ padding: 16, gap: 12 }}
          onScroll={(e) => {
            const { layoutMeasurement, contentOffset, contentSize } = e.nativeEvent;
            const distanceFromBottom =
              contentSize.height - (contentOffset.y + layoutMeasurement.height);
            onAtBottomChange(distanceFromBottom < 24);
            onScrollY(contentOffset.y);
          }}
          scrollEventThrottle={64}
          onContentSizeChange={() => {
            if (!initialScrollDoneRef.current) {
              chatScrollRef.current?.scrollToEnd({ animated: false });
              setTimeout(() => {
                initialScrollDoneRef.current = true;
              }, 60);
            }
          }}
        >
          {loading ? (
            <View className="items-center py-12">
              <ActivityIndicator size="small" color={COLORS.FOREST.hex} />
            </View>
          ) : messages.length === 0 ? (
            <View className="items-center py-16">
              <Text
                className="text-[13px]"
                style={{ fontFamily: FONTS.body, color: STUDENT_THEME.textOnSurfaceFaint }}
              >
                {"No messages yet — say hi to " + other.name + "."}
              </Text>
            </View>
          ) : (
            groups.map((g) => {
              const showDivider =
                firstUnreadId != null && g.messages[0].id === firstUnreadId;
              const isMine = myUserId === g.sender_id;
              const first = g.messages[0];
              const senderName = first.sender_name ?? (isMine ? "You" : other.name);
              return (
                <View
                  key={"g:" + g.id}
                  onLayout={
                    showDivider
                      ? (e) => {
                          const y = e.nativeEvent.layout.y;
                          onSetUnreadAnchorY(y);
                        }
                      : undefined
                  }
                >
                  {showDivider ? <UnreadDivider /> : null}
                  <View className="flex-row items-start gap-3">
                    <View
                      style={
                        Platform.OS === "web"
                          ? ({
                              width: 30,
                              position: "sticky",
                              top: 0,
                              alignSelf: "flex-start",
                              zIndex: 2,
                              paddingTop: 4,
                              paddingBottom: 4,
                              backgroundColor: STUDENT_THEME.surfaceRaisedBg,
                            } as unknown as object)
                          : { width: 30, alignSelf: "flex-start" as const }
                      }
                    >
                      <Avatar
                        name={senderName}
                        imageUrl={first.sender_avatar_url}
                        size="sm"
                      />
                    </View>
                    <View className="min-w-0 flex-1">
                      <View className="flex-row items-baseline gap-2 pt-1">
                        <Text
                          className="text-[13px] text-forest"
                          style={{ fontFamily: FONTS.bodySemiBold }}
                          numberOfLines={1}
                        >
                          {isMine ? senderName + " (you)" : senderName}
                        </Text>
                        <Text
                          className="text-[10px]"
                          style={{
                            fontFamily: FONTS.body,
                            color: STUDENT_THEME.textOnSurfaceFaint,
                            letterSpacing: 0.4,
                          }}
                        >
                          {formatTimestamp(first.created_at)}
                        </Text>
                      </View>
                      <View style={{ paddingTop: 4, gap: 6 }}>
                        {g.messages.map((m) => (
                          <DmMessageRow
                            key={m.id}
                            m={m}
                            isMine={isMine}
                            myUserId={myUserId}
                            senderNameForLink={senderName}
                            onPressLink={onPressLink}
                            onReply={onReply}
                            onDelete={onDelete}
                          />
                        ))}
                      </View>
                    </View>
                  </View>
                </View>
              );
            })
          )}
        </ScrollView>

        {showJumpToUnread ? (
          <Pressable
            onPress={onJumpToUnread}
            style={[
              {
                position: "absolute",
                top: 12,
                right: 18,
                flexDirection: "row",
                alignItems: "center",
                gap: 8,
                paddingHorizontal: 14,
                paddingVertical: 9,
                borderRadius: 999,
                backgroundColor: COLORS.FOREST.hex,
                borderWidth: 1,
                borderColor: "rgba(181,135,58,0.55)",
                zIndex: 5,
              },
              Platform.OS === "web"
                ? ({
                    cursor: "pointer",
                    boxShadow: "0 12px 24px -10px rgba(0,0,0,0.45)",
                  } as unknown as ViewStyle)
                : null,
            ]}
          >
            <Svg width={11} height={11} viewBox="0 0 12 12" fill="none">
              <Path
                d="M6 10V2M2.5 5.5L6 2l3.5 3.5"
                stroke={COLORS.AMBER.hex}
                strokeWidth={1.6}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Svg>
            <Text
              className="text-[11px] uppercase"
              style={{
                fontFamily: FONTS.bodyBold,
                color: COLORS.AMBER.hex,
                letterSpacing: 1.4,
              }}
            >
              Jump to unread
            </Text>
          </Pressable>
        ) : null}
      </View>

      {/* Composer: pinned bottom of the DM panel. */}
      <View
        className="flex-col gap-1 border-t border-sand-dark px-3 py-2.5"
        style={{ backgroundColor: "rgba(28,43,26,0.02)", flexShrink: 0 }}
      >
        {replyTo ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              paddingHorizontal: 10,
              paddingVertical: 6,
              marginBottom: 6,
              borderLeftWidth: 2,
              borderLeftColor: COLORS.AMBER.hex,
              backgroundColor: "rgba(181,135,58,0.08)",
              borderRadius: 4,
            }}
          >
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 10.5, letterSpacing: 1.2, color: COLORS.AMBER.hex }}>
                REPLYING TO {(replyTo.sender_name ?? "MEMBER").toUpperCase()}
              </Text>
              <Text
                style={{ fontFamily: FONTS.body, fontSize: 12, color: STUDENT_THEME.textOnSurfaceDim }}
                numberOfLines={1}
              >
                {replyTo.body}
              </Text>
            </View>
            <Pressable
              onPress={() => onClearReply?.()}
              accessibilityRole="button"
              accessibilityLabel="Cancel reply"
              hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
              style={[
                { width: 22, height: 22, alignItems: "center", justifyContent: "center", borderRadius: 4 },
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Svg width={10} height={10} viewBox="0 0 12 12" fill="none">
                <Path d="M2 2 L10 10 M10 2 L2 10" stroke={STUDENT_THEME.textOnSurfaceDim} strokeWidth={1.4} strokeLinecap="round" />
              </Svg>
            </Pressable>
          </View>
        ) : null}
        <View className="flex-row items-center gap-2">
          <TextInput
            ref={composerRef}
            value={draft}
            onChangeText={(next) => setDraft(clampToWordCap(draft, next, CHAT_WORD_CAP))}
            placeholder={"Message " + other.name + "…"}
            placeholderTextColor={STUDENT_THEME.textOnSurfaceFaint}
            multiline
            editable={other.threadId != null}
            style={{
              flex: 1,
              minHeight: 38,
              maxHeight: 140,
              paddingHorizontal: 12,
              paddingVertical: 10,
              fontFamily: FONTS.body,
              fontSize: 14,
              color: COLORS.FOREST.hex,
              backgroundColor: "transparent",
              outlineStyle: "none",
            } as unknown as object}
            onKeyPress={(e) => {
              const ne = e.nativeEvent as unknown as { key?: string; shiftKey?: boolean };
              if (Platform.OS === "web" && ne.key === "Enter" && !ne.shiftKey) {
                e.preventDefault?.();
                onSend();
              }
            }}
          />
          <SendPlaneButton
            onPress={onSend}
            disabled={sending || draft.trim().length === 0 || other.threadId == null}
            sending={sending}
          />
        </View>
      </View>
    </>
  );
}
